import React, { useEffect, useState, useContext } from "react";
import { Tag, Typography, Button, Modal, Card, Steps, Divider, message, Input, DatePicker, TimePicker, Space, Radio, Checkbox, Select, Skeleton } from "antd";
import io from "socket.io-client";
import { CloseCircleOutlined, InfoCircleOutlined, DownloadOutlined, HeartOutlined, AppstoreOutlined, PhoneOutlined, MailOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { CartContext } from "../CartContext";
import OrdersServ from "../service/OrdersServ";
import InvoiceServ from "../service/InvoiceServ";
import CartServ from "../service/CartServ";
import UserServ from "../service/UserServ";
import Breadcrumbs from "../component/Breadcrumbs";
import { usePreference } from "../context/PreferenceContext";
import dayjs from "dayjs";
import ProductServ from "../service/ProductServ";
import {
    IconAlertCircle,
    IconClock,
    IconCalendar,
    IconTruck,
    IconCalendarStats
} from "@tabler/icons-react";
import { formatDate } from "../util/dateUtils";
import { formatUnit as localFormatUnit, capitalizeFirst } from "../util/unitFormatter";

import "../css/orders.css";
import DataTable from "../component/ui/DataTable";
import SafeImage, { getImageUrl } from "../component/SafeImage";
import { useUploadConfig } from "../util/useUploadConfig";

const { Title, Text } = Typography;
const { Step } = Steps;

const DeliveryCountdown = ({ createdAt, minutes, label, isActive = true }) => {
    const [timeLeft, setTimeLeft] = useState(0);

    useEffect(() => {
        if (!isActive) return;
        const update = () => {
            const diff = dayjs(createdAt).add(minutes, 'minute').diff(dayjs(), 'second');
            setTimeLeft(diff > 0 ? diff : 0);
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [createdAt, minutes, isActive]);

    if (!isActive) return <span>{label}</span>;
    if (timeLeft <= 0) return <span>{label} (Arriving Soon)</span>;

    const h = Math.floor(timeLeft / 3600);
    const m = Math.floor((timeLeft % 3600) / 60);
    const s = timeLeft % 60;

    const timeStr = [
        h > 0 ? h.toString().padStart(2, '0') : null,
        m.toString().padStart(2, '0'),
        s.toString().padStart(2, '0')
    ].filter(Boolean).join(':');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            <span>{label}</span>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: '#fff7ed',
                padding: '4px 8px',
                borderRadius: 6,
                border: '1px solid #ffedd5',
                width: 'fit-content'
            }}>
                <span style={{ fontSize: 12 }}>⏱️</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#c2410c', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                    {timeStr}
                </span>
            </div>
        </div>
    );
};

const CustomerOrders = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { formatPrice, t } = usePreference();
    const { clearCart, refreshCart } = useContext(CartContext);
    const [orders, setOrders] = useState([]);
    const { getProfile } = useUploadConfig();
    const genericProfile = getProfile('GENERIC');
    const maxUploadSizeMB = genericProfile?.maxUploadMB || 3;
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
    const [loading, setLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [actionLoading, setActionLoading] = useState(false);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [cancellationReasonOption, setCancellationReasonOption] = useState("");
    const [filterCategory, setFilterCategory] = useState("all");
    const [filterSpecificStatus, setFilterSpecificStatus] = useState("all");
    const [returnModalOpen, setReturnModalOpen] = useState(false);
    const [refundPopupOpen, setRefundPopupOpen] = useState(false);
    const [selectedRefundMethod, setSelectedRefundMethod] = useState('');
    const [savedProfile, setSavedProfile] = useState(null);
    const [useSavedDetails, setUseSavedDetails] = useState(true);
    const [upiIdInput, setUpiIdInput] = useState('');
    const [bankDetailsInput, setBankDetailsInput] = useState({
        bankName: '',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: ''
    });
    const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
    const [rescheduleDate, setRescheduleDate] = useState(null);
    const [rescheduleSlot, setRescheduleSlot] = useState(null);
    const [rescheduleTime, setRescheduleTime] = useState(null);
    const [issueModalOrder, setIssueModalOrder] = useState(null);
    const [uncheckedItems, setUncheckedItems] = useState([]);
    const [rejectedIssueProducts, setRejectedIssueProducts] = useState([]);
    const [shopProducts, setShopProducts] = useState([]);
    const [fetchingProducts, setFetchingProducts] = useState(false);
    const [addedProducts, setAddedProducts] = useState([]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let stateChanged = false;
        const newState = { ...location.state };

        if (location.state?.highlightOrderId && orders.length > 0) {
            const order = orders.find(o => o._id === location.state.highlightOrderId);
            if (order) {
                setSelectedOrder(order);
                setIsModalOpen(true);
                delete newState.highlightOrderId;
                stateChanged = true;
            }
        }
        if (location.state?.openIssueOrderId && orders.length > 0) {
            const order = orders.find(o => o._id === location.state.openIssueOrderId);
            if (order) {
                setIssueModalOrder(order);
                delete newState.openIssueOrderId;
                stateChanged = true;
            }
        }

        if (stateChanged) {
            navigate(location.pathname, { replace: true, state: newState });
        }
    }, [location.state, orders, navigate]);


    const handleCategoryChange = (val) => {
        setFilterCategory(val);
        setFilterSpecificStatus("all");
    };

    const getStatusOptions = () => {
        let options = [{ label: 'All Statuses', value: 'all' }];

        if (filterCategory === 'all' || filterCategory === 'active') {
            options.push(
                { label: 'Ordered', value: 'ordered' },
                { label: 'Confirmed', value: 'confirmed' },
                { label: 'Packed', value: 'packed' },
                { label: 'Out for Delivery', value: 'picked_up' }
            );
        }
        if (filterCategory === 'all' || filterCategory === 'delivered') {
            options.push({ label: 'Delivered', value: 'delivered' });
        }
        if (filterCategory === 'all' || filterCategory === 'cancelled') {
            options.push(
                { label: 'Cancelled', value: 'cancelled' },
                { label: 'Return Requested', value: 'return_requested' },
                { label: 'Return Picked Up', value: 'return_picked_up' },
                { label: 'Refund Processing', value: 'refund_processing' },
                { label: 'Refund Completed', value: 'refund_completed' }
            );
        }
        return options;
    };


    useEffect(() => {
        if (issueModalOrder?.shopId) {
            const shopId = typeof issueModalOrder.shopId === 'object' ? issueModalOrder.shopId._id : issueModalOrder.shopId;
            if (shopId) {
                setFetchingProducts(true);
                ProductServ.getByShop(shopId, { limit: 8 })
                    .then(res => {
                        setShopProducts(res.data || []);
                    })
                    .catch(() => setShopProducts([]))
                    .finally(() => setFetchingProducts(false));
            }
        } else {
            setShopProducts([]);
        }
    }, [issueModalOrder]);




    // --- Suggestion Card Component (Replicating Home ProductCard Style) ---
    const SuggestionProductCard = ({ product, onAdd, shopName }) => {
        const allVariants = React.useMemo(() => {
            let variants = [];
            if (product.options && product.options.length > 0) {
                product.options.forEach(opt => {
                    if (opt.variants) variants.push(...opt.variants.map(v => ({ ...v, optionName: opt.name })));
                });
            } else if (product.hasVariants && product.variants) {
                variants = product.variants;
            }
            return [...variants].sort((a, b) => (a.price || 0) - (b.price || 0));
        }, [product]);

        const [selectedVariantId, setSelectedVariantId] = useState(allVariants.length > 0 ? (allVariants[0]._id || allVariants[0].val || allVariants[0].value) : null);
        const [showAllVariants, setShowAllVariants] = useState(false);

        const currentVariant = React.useMemo(() => {
            if (!allVariants.length) return null;
            return allVariants.find(v => String(v._id || v.val || v.value) === String(selectedVariantId)) || allVariants[0];
        }, [allVariants, selectedVariantId]);

        const displayPrice = currentVariant ? (currentVariant.price || currentVariant.pri) : product.price;
        const displayMrp = currentVariant ? (currentVariant.mrp || product.mrp) : product.mrp;

        return (
            <Card
                hoverable
                className="product-card-unified pcu-card-body-no-padding"
                style={{ height: '100%', maxWidth: 'none' }}
            >
                <div className="pcu-image-container" style={{ height: 120 }}>
                    <SafeImage src={currentVariant?.imageUrl || product.images?.[0] || product.imageUrl} alt={product.name} style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
                    <div className="pcu-wishlist-icon">
                        <HeartOutlined style={{ fontSize: 14 }} />
                    </div>
                    <div className="pcu-add-btn-wrapper">
                        <Button
                            className="pcu-add-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAdd(product, currentVariant);
                            }}
                        >
                            ADD
                        </Button>
                    </div>
                </div>
                <div className="pcu-content" style={{ padding: '8px 10px' }}>
                    <div className="pcu-title" style={{ fontSize: 13, height: 32, marginBottom: 4 }}>
                        {capitalizeFirst(product.name)}
                    </div>
                    <div className="pcu-delivery-wrapper">
                        <div className="pcu-delivery-badge-orange" style={{ fontSize: 9 }}>
                            <IconTruck size={12} style={{ marginRight: 4, display: 'inline' }} />
                            Delivery By 18 mins
                        </div>
                    </div>
                    <div className="pcu-price-row" style={{ marginTop: 4 }}>
                        <span className="pcu-price" style={{ fontSize: 14 }}>{formatPrice(displayPrice)}</span>
                        {displayMrp > displayPrice && (
                            <span className="pcu-mrp" style={{ fontSize: 10 }}>MRP {formatPrice(displayMrp)}</span>
                        )}
                    </div>
                    <div className="pcu-variant-selector-wrapper" style={{ marginTop: 6, minHeight: 24 }}>
                        {allVariants.length > 0 && (
                            <div className="pcu-variant-pills-container">
                                {allVariants.slice(0, showAllVariants ? allVariants.length : 1).map(v => {
                                    const isSelected = String(v._id || v.val || v.value) === String(selectedVariantId);
                                    return (
                                        <div
                                            key={v._id || v.val || v.value}
                                            className={`pcu-variant-pill ${isSelected ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedVariantId(v._id || v.val || v.value);
                                            }}
                                            style={{ fontSize: 9, padding: '1px 6px' }}
                                        >
                                            {localFormatUnit(v.val || v.value, v.optionName || product.variantType || "Option")}
                                        </div>
                                    );
                                })}
                                {!showAllVariants && allVariants.length > 1 && (
                                    <div
                                        className="pcu-variant-pill"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowAllVariants(true);
                                        }}
                                        style={{ fontSize: 9, padding: '1px 6px' }}
                                    >
                                        +{allVariants.length - 1} more
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Card>
        );
    };

    const fetchOrders = async (page = 1, showLoading = true) => {
        try {
            if (showLoading && orders.length === 0) setLoading(true);
            const cached = localStorage.getItem('cached_customer_orders_page_' + page);
            if (cached && showLoading) {
                try {
                    const parsed = JSON.parse(cached);
                    setOrders(parsed.ordersList);
                    if (parsed.pagination) setPagination(parsed.pagination);
                    setLoading(false); // Stop showing skeleton
                } catch (e) { }
            }

            const res = await OrdersServ.getMyOrders({ page, limit: pagination.limit || 10 });
            const ordersList = res?.data || (Array.isArray(res) ? res : []);
            const newPagination = res?.pagination || { page, limit: 10, total: ordersList.length, pages: 1 };

            localStorage.setItem('cached_customer_orders_page_' + page, JSON.stringify({ ordersList, pagination: newPagination }));
            setPagination(newPagination);



            setOrders(ordersList);
            setSelectedOrder(prev => {
                if (!prev) return prev;
                const found = ordersList.find(o => o._id === prev._id);
                return found || prev;
            });
        } catch (error) {
            console.error("Fetch Orders Error:", error);
            message.error("Failed to load your orders");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(1, true);

        // Socket Listener for Real-time Status Updates
        const token = localStorage.getItem("token") || localStorage.getItem("customerToken");
        let socket;

        if (token) {
            try {
                const socketUrl = 'http://localhost:4000';
                socket = io(socketUrl);

                // Extract user ID from token
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const payload = JSON.parse(window.atob(base64));
                const userId = payload.id || payload._id;

                if (userId) {
                    socket.emit('join', userId);
                    socket.on('orderStatusUpdated', (data) => {
                        console.log("🚀 Socket - Order Status Updated:", data);
                        // Refresh orders to get latest state including history etc.
                        fetchOrders();

                        // Show a helpful notification
                        const statusLabel = data.status.replace(/_/g, ' ').toUpperCase();
                        message.info(`Order #${data.orderId || data.order?.orderId} is now ${statusLabel}`);
                    });
                }
            } catch (e) {
                console.error("Socket connection failed in CustomerOrders", e);
            }
        }
        return () => {
            if (socket) socket.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const showCancelConfirmation = () => {
        setCancelReason("");
        setCancellationReasonOption("");
        setCancelModalOpen(true);
    };

    const handleCancelOrder = async () => {
        if (!selectedOrder) return;

        try {
            setActionLoading(true);
            const reason = cancelReason.trim() || "Cancelled by user via dashboard";

            // Optimistic update
            setOrders(prev => {
                const updated = prev.map(o => String(o._id) === String(selectedOrder._id) ? { ...o, status: 'cancelled' } : o);
                return [...updated];
            });

            // Cancel the order — backend handles RefundRequest creation and refund
            await OrdersServ.cancelOrder(selectedOrder._id, reason);

            message.success("Order cancelled successfully");
            window.dispatchEvent(new Event("credits-refresh"));
            setCancelModalOpen(false);
            setIsModalOpen(false);

            // Re-fetch to sync with backend definitively
            fetchOrders();
        } catch (error) {
            console.error(error);
            message.error(error.message || "Failed to cancel order");
        } finally {
            setActionLoading(false);
        }
    };

    const [returnItemData, setReturnItemData] = useState({});
    const [returnValidationErrors, setReturnValidationErrors] = useState({});
    const [refundValidationErrors, setRefundValidationErrors] = useState({});

    const showReturnConfirmation = async (order = null) => {
        const targetOrder = order && order._id ? order : selectedOrder;
        if (targetOrder) {
            const deliveredDate = targetOrder.deliveredAt || targetOrder.updatedAt;
            const isExpired = deliveredDate ? ((Date.now() - new Date(deliveredDate).getTime()) / (1000 * 3600 * 24) > 7) : false;
            if (isExpired) {
                message.error("The return window (7 days) for this order has expired.");
                return;
            }
            setSelectedOrder(targetOrder);
            const initialData = {};
            targetOrder.items.forEach((item, idx) => {
                initialData[idx] = {
                    selected: false,
                    reason: "",
                    description: "",
                    quantity: item.quantity || 1,
                    images: [],
                    previews: []
                };
            });
            setReturnItemData(initialData);
        }
        setReturnValidationErrors({});
        setRefundValidationErrors({});
        setSelectedRefundMethod('');
        setUpiIdInput('');
        setUseSavedDetails(true);
        setBankDetailsInput({ bankName: '', accountHolderName: '', accountNumber: '', ifscCode: '' });
        setRefundPopupOpen(false);

        try {
            const profileRes = await UserServ.getProfile();
            if (profileRes && profileRes.data) {
                setSavedProfile(profileRes.data);
            }
        } catch (err) {
            console.error("Failed to load user profile for return details", err);
        }

        setReturnModalOpen(true);
    };

    const handleItemImageUpload = (idx, e) => {
        const files = Array.from(e.target.files);
        setReturnItemData(prev => {
            const itemData = { ...prev[idx] };
            if (itemData.images.length + files.length > 3) {
                message.error("You can only upload up to 3 images per item");
                return prev;
            }

            const validFiles = files.filter(file => {
                const isLtLimit = file.size / 1024 / 1024 < maxUploadSizeMB;
                if (!isLtLimit) {
                    message.error(`Image ${file.name} must be smaller than ${maxUploadSizeMB}MB!`);
                }
                return isLtLimit;
            });

            if (validFiles.length) {
                itemData.images = [...itemData.images, ...validFiles];
                const newPreviews = validFiles.map(file => URL.createObjectURL(file));
                itemData.previews = [...itemData.previews, ...newPreviews];
            }
            return { ...prev, [idx]: itemData };
        });
        e.target.value = null;
    };

    const removeItemImage = (idx, imgIndex) => {
        setReturnItemData(prev => {
            const itemData = { ...prev[idx] };
            itemData.images = itemData.images.filter((_, i) => i !== imgIndex);
            itemData.previews = itemData.previews.filter((_, i) => i !== imgIndex);
            return { ...prev, [idx]: itemData };
        });
    };

    const handleOpenRefundDetails = () => {
        if (!selectedOrder) return;

        const selectedIndices = Object.keys(returnItemData).filter(idx => returnItemData[idx]?.selected);
        if (selectedIndices.length === 0) {
            message.warning("Please select at least one item to return");
            return;
        }

        let hasErrors = false;
        const newErrors = {};

        for (let idx of selectedIndices) {
            const data = returnItemData[idx] || { reason: "", description: "", images: [], quantity: 1 };
            const originalItem = selectedOrder?.items?.[idx];
            if (!originalItem) continue;

            const itemErrors = {};

            if (!data.reason?.trim()) {
                itemErrors.reason = "Please select a reason";
                hasErrors = true;
            } else if (!data.description || !data.description.trim()) {
                itemErrors.description = "Please describe the issue";
                hasErrors = true;
            }

            if ((data.images || []).length === 0) {
                itemErrors.images = "Please upload at least 1 photo";
                hasErrors = true;
            }

            const returnQty = parseInt(data.quantity, 10);
            if (isNaN(returnQty) || returnQty < 1 || returnQty > originalItem.quantity) {
                itemErrors.quantity = "Invalid quantity";
                hasErrors = true;
            }

            if (Object.keys(itemErrors).length > 0) {
                newErrors[idx] = itemErrors;
            }
        }

        setReturnValidationErrors(newErrors);

        if (hasErrors) {
            return;
        }

        setRefundPopupOpen(true);
    };

    const handleReturnRequest = async () => {
        if (!selectedOrder) return;

        setRefundValidationErrors({});

        if (!selectedRefundMethod) {
            message.warning("Please select a refund method");
            return;
        }

        let upiToSend = null;
        let bankToSend = null;
        let hasErrors = false;
        const newErrors = {};

        if (selectedRefundMethod === 'UPI') {
            if (useSavedDetails && savedProfile?.upiId) {
                upiToSend = savedProfile.upiId;
            } else {
                const upiVal = upiIdInput?.trim();
                if (!upiVal) {
                    newErrors.upiId = "Please enter a UPI ID";
                    hasErrors = true;
                } else {
                    const upiRegex = /^[\w.\-_]+@[\w.\-_]+$/;
                    if (!upiRegex.test(upiVal)) {
                        newErrors.upiId = "Please enter a valid UPI ID format (e.g. user@bank)";
                        hasErrors = true;
                    }
                }
                upiToSend = upiVal;
            }
        } else if (selectedRefundMethod === 'BANK') {
            if (useSavedDetails && savedProfile?.bankDetails?.accountNumber) {
                bankToSend = savedProfile.bankDetails;
            } else {
                const { bankName, accountHolderName, accountNumber, ifscCode } = bankDetailsInput;
                if (!bankName?.trim()) {
                    newErrors.bankName = "Bank Name is required";
                    hasErrors = true;
                }
                if (!accountHolderName?.trim()) {
                    newErrors.accountHolderName = "Account Holder Name is required";
                    hasErrors = true;
                }
                if (!accountNumber?.trim()) {
                    newErrors.accountNumber = "Account Number is required";
                    hasErrors = true;
                } else if (accountNumber.trim().length < 9 || accountNumber.trim().length > 18) {
                    newErrors.accountNumber = "Bank Account Number should be between 9 and 18 digits";
                    hasErrors = true;
                }
                if (!ifscCode?.trim()) {
                    newErrors.ifscCode = "IFSC Code is required";
                    hasErrors = true;
                } else {
                    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
                    if (!ifscRegex.test(ifscCode.trim().toUpperCase())) {
                        newErrors.ifscCode = "Please enter a valid IFSC code (11 characters, 5th character must be '0')";
                        hasErrors = true;
                    }
                }

                if (!hasErrors) {
                    bankToSend = {
                        bankName: bankName.trim(),
                        accountHolderName: accountHolderName.trim(),
                        accountNumber: accountNumber.trim(),
                        ifscCode: ifscCode.trim().toUpperCase()
                    };
                }
            }
        }

        if (hasErrors) {
            setRefundValidationErrors(newErrors);
            message.error("Please fix the validation errors in the form.");
            return;
        }

        const selectedIndices = Object.keys(returnItemData).filter(idx => returnItemData[idx]?.selected);
        if (selectedIndices.length === 0) {
            message.warning("Please select at least one item to return");
            return;
        }

        const itemsPayload = [];
        let totalRefund = 0;

        for (let idx of selectedIndices) {
            const data = returnItemData[idx] || { reason: "", description: "", images: [], quantity: 1 };
            const originalItem = selectedOrder?.items?.[idx];
            if (!originalItem) continue;

            const returnQty = parseInt(data.quantity, 10);
            itemsPayload.push({
                product: originalItem.product._id || originalItem.product,
                name: originalItem.name,
                quantity: returnQty,
                price: originalItem.price,
                returnReason: data.reason,
                description: data.description,
                _idx: idx
            });
            totalRefund += (originalItem.price * returnQty);
        }

        try {
            setActionLoading(true);
            const formData = new FormData();
            formData.append('orderId', selectedOrder._id);
            formData.append('type', 'return');
            formData.append('refundAmount', totalRefund);
            formData.append('refundMethod', selectedRefundMethod);

            if (selectedRefundMethod === 'UPI') {
                formData.append('upiId', upiToSend);
            } else if (selectedRefundMethod === 'BANK') {
                formData.append('bankDetails[bankName]', bankToSend.bankName);
                formData.append('bankDetails[accountHolderName]', bankToSend.accountHolderName);
                formData.append('bankDetails[accountNumber]', bankToSend.accountNumber);
                formData.append('bankDetails[ifscCode]', bankToSend.ifscCode);
            }

            itemsPayload.forEach((item, index) => {
                const itemIdx = item._idx;
                delete item._idx; // Clean before sending
                formData.append(`items[${index}][productId]`, item.product);
                formData.append(`items[${index}][product]`, item.product);
                formData.append(`items[${index}][name]`, item.name || '');
                formData.append(`items[${index}][quantity]`, item.quantity);
                formData.append(`items[${index}][returnQty]`, item.quantity);
                formData.append(`items[${index}][price]`, item.price);
                formData.append(`items[${index}][returnReason]`, item.returnReason);
                formData.append(`items[${index}][description]`, item.description || '');

                const data = returnItemData[itemIdx] || {};
                const imagesArr = data.images || [];
                formData.append(`items[${index}][imageCount]`, imagesArr.length);
                imagesArr.forEach(img => {
                    formData.append('images', img); // Backend will process this
                });
            });

            await OrdersServ.submitReturnRequest(formData);
            message.success("Return request submitted successfully");
            setReturnModalOpen(false);
            setRefundPopupOpen(false);
            setIsModalOpen(false);
            fetchOrders();
        } catch (error) {
            console.error("Submit Return Request Error:", error);
            message.error(error.message || "Failed to submit return request");
        } finally {
            setActionLoading(false);
        }
    };

    const handleReschedule = async () => {
        if (!selectedOrder || !rescheduleDate || !rescheduleSlot) {
            message.warning("Please select both date and time slot");
            return;
        }

        try {
            setActionLoading(true);
            const payload = {
                scheduledDate: rescheduleDate.toISOString(),
                scheduledTime: rescheduleSlot === 'custom'
                    ? (rescheduleTime ? rescheduleTime.format("HH:mm") : null)
                    : rescheduleSlot,
                deliveryCharge: rescheduleSlot === 'custom' ? 100 : 0
            };

            await OrdersServ.rescheduleOrder(selectedOrder._id, payload);
            message.success("Delivery rescheduled successfully");
            setRescheduleModalOpen(false);
            setIsModalOpen(false);
            fetchOrders();
        } catch (error) {
            message.error(error.message || "Failed to reschedule delivery");
        } finally {
            setActionLoading(false);
        }
    };

    const showRescheduleConfirmation = (order = null) => {
        if (order && order._id) {
            setSelectedOrder(order);
        }
        setRescheduleDate(dayjs());
        setRescheduleSlot(null);
        setRescheduleTime(null);
        setRescheduleModalOpen(true);
    };


    const handleTimeChangeRespond = async (response) => {
        if (!selectedOrder) return;
        try {
            setActionLoading(true);
            await OrdersServ.respondToDeliveryTimeChange(selectedOrder._id, response);
            message.success(`Delivery time change ${response} successfully`);
            setIsModalOpen(false);
            fetchOrders();
        } catch (error) {
            message.error(error.message || `Failed to ${response} request`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleProductIssueRespond = async (response) => {
        // Support both selectedOrder (if from details page) and issueModalOrder (if from drawer)
        const targetOrder = issueModalOrder || selectedOrder;
        if (!targetOrder) return;
        try {
            setActionLoading(true);
            const addPayload = addedProducts.map(p => ({ productId: p.product._id, quantity: p.quantity }));
            await OrdersServ.buyerRespondToUpdate(targetOrder._id, response, uncheckedItems, rejectedIssueProducts, addPayload);
            message.success(`Request ${response.toLowerCase()}ed successfully`);
            if (issueModalOrder) {
                setIssueModalOrder(null);
                setUncheckedItems([]);
                setRejectedIssueProducts([]);
                setAddedProducts([]);
            }
            setIsModalOpen(false);
            fetchOrders();
            // Refresh credits if user accepted (since refunds might happen)
            if (response === 'ACCEPT') {
                window.dispatchEvent(new Event("credits-refresh"));
            }
        } catch (error) {
            message.error(error.message || `Failed to ${response} request`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleReorder = async () => {
        if (!selectedOrder) return;

        try {
            setActionLoading(true);
            const key = "reorder_msg";
            message.loading({ content: "Adding items to cart...", key });

            // 1. Clear cart first to avoid mix-up (as per usual re-order logic)
            await clearCart();

            // 2. Add each item from the old order to the cart
            // Backend will validate status, price, and stock during this call
            const addPromises = selectedOrder.items.map(item =>
                CartServ.addToCart(item.product._id || item.product, item.quantity, item.selectedOptions || {})
            );

            await Promise.all(addPromises);
            await refreshCart();

            message.success({ content: "Items added to cart! Redirecting...", key });

            // 3. Navigate to checkout/cart
            setIsModalOpen(false);
            navigate("/checkout");
        } catch (error) {
            console.error("Re-order Error:", error);
            message.error("Failed to re-order. Some items may be unavailable.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownloadInvoice = async (orderId) => {
        try {
            message.loading({ content: "Preparing your invoice...", key: "download_invoice" });
            const response = await InvoiceServ.download(orderId);

            // Create a link and trigger download
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Invoice_${selectedOrder?.orderId || orderId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            message.success({ content: "Invoice downloaded successfully!", key: "download_invoice" });
        } catch (error) {
            console.error("Download Error:", error);
            message.error({ content: error.message || "Failed to download invoice", key: "download_invoice" });
        }
    };

    // --- Return Status Priority Logic ---
    const getEffectiveStatus = (order) => {
        if (order?.paymentStatus?.toLowerCase() === 'refunded') {
            return 'refund_completed';
        }

        const rs = order?.returnRequest?.status || order?.returnStatus;
        if (!rs || rs === 'none') {
            if (order?.productIssueRequest?.status === 'ACCEPTED' && ['ordered', 'confirmed', 'packed'].includes(order?.status?.toLowerCase())) {
                return 'order_updated';
            }
            return order?.status?.toLowerCase();
        }

        // Map linear statuses to readable lowercase versions for the customer
        const statusMap = {
            RETURN_REQUESTED: 'return_requested',
            RETURN_APPROVED_BY_ADMIN: 'return_approved',
            RETURN_REJECTED_BY_ADMIN: 'return_rejected',
            RETURN_ACCEPTED_BY_SELLER: 'return_accepted',
            RETURN_REJECTED_BY_SELLER: 'return_rejected',
            RETURN_PICKUP_PENDING: 'return_approved',
            RETURN_PICKUP_ASSIGNED: 'return_pickup_assigned',
            RETURN_PICKUP_ACCEPTED: 'return_pickup_accepted',
            RETURN_PICKED_UP: 'return_picked_up',
            RETURN_PICKUP_REJECTED: 'return_pickup_rejected',
            RETURN_IN_TRANSIT: 'return_in_transit',
            RETURN_DELIVERED_TO_SELLER: 'return_delivered_to_seller',
            RETURN_RECEIVED_VERIFIED: 'return_received_verified',
            RETURN_RECEIVED_FAILED: 'return_received_failed',
            RETURN_COMPLETED: 'return_received_verified',
            REFUND_PENDING: 'refund_processing',
            REFUND_PROCESSING: 'refund_processing',
            REFUND_COMPLETED: 'refund_completed',
            // legacy lowercase
            requested: 'return_requested',
            approved: 'return_approved',
            rejected: 'return_rejected',
            picked_up: 'return_picked_up',
            refund_completed: 'refund_completed'
        };

        return statusMap[rs.toUpperCase()] || rs.toLowerCase();
    };

    const getStatusColor = (status) => {
        const map = {
            ordered: "default",      // Grey
            confirmed: "#722ed1",
            packed: "#1890ff",       // Blue
            picked_up: "#faad14",   // Orange
            out_for_delivery: "#faad14", // Legacy - same as picked_up
            delivered: "#52c41a",     // Green
            cancelled: "#ff4d4f",

            return_requested: "#fa8c16",
            return_approved: "#1890ff",
            return_accepted: "#722ed1",
            return_rejected: "#ff4d4f",
            return_pickup_assigned: "#faad14",
            return_pickup_accepted: "#faad14",
            return_picked_up: "#fa8c16",
            return_pickup_rejected: "#ff4d4f",
            return_in_transit: "#faad14",
            return_delivered_to_seller: "#52c41a",
            return_received_verified: "#52c41a",
            return_received_failed: "#ff4d4f",
            refund_processing: "#1890ff",
            refund_completed: "#52c41a",

            order_updated: "#52c41a",
        };
        return map[status] || map[status?.toLowerCase()] || "default";
    };

    const getStatusLabel = (status) => {
        const s = status?.toLowerCase();
        const map = {
            ordered: t('ordered', 'Ordered'),
            confirmed: t('confirmed', 'Confirmed'),
            packed: t('packed', 'Packed'),
            picked_up: t('picked_up', 'Out for Delivery'),
            out_for_delivery: t('picked_up', 'Out for Delivery'),
            delivered: t('delivered', 'Delivered'),
            cancelled: t('cancelled', 'Cancelled'),
            returned: t('returned', 'Returned'),

            return_requested: t('return_requested', 'Return Requested'),
            return_approved: t('return_approved', 'Return Approved'),
            return_accepted: t('return_accepted', 'Return Accepted'),
            return_rejected: t('return_rejected', 'Return Rejected'),
            return_pickup_assigned: t('return_pickup_assigned', 'Return Pickup Assigned'),
            return_pickup_accepted: t('return_pickup_accepted', 'Return Pickup Accepted'),
            return_picked_up: t('return_picked_up', 'Return Picked Up'),
            return_pickup_rejected: t('return_pickup_rejected', 'Return Pickup Rejected'),
            return_in_transit: t('return_in_transit', 'Return In Transit'),
            return_delivered_to_seller: t('return_delivered_to_seller', 'Return Completed'),
            return_received_verified: t('return_received_verified', 'Return Received & Verified'),
            return_received_failed: t('return_received_failed', 'Verification Failed'),
            refund_processing: t('refund_processing', 'Refund Processing'),
            refund_completed: t('refund_completed', 'Refund Completed'),

            order_updated: t('order_updated', 'Replace The Order'),
        };
        return map[s] || map[status] || status;
    };

    const getStatusStep = (status) => {
        const current = status?.toLowerCase();
        // Return -1 for terminal / non-progress statuses so nothing is highlighted
        const isTerminalOrReturn = ['cancelled', 'delivery_missed', 'returned', 'refund_completed'].includes(current) ||
            current.startsWith('return_') || current.startsWith('refund_');
        if (!current || isTerminalOrReturn) {
            return -1;
        }
        if (current === 'ordered') return 0;
        if (current === 'confirmed') return 1;
        if (current === 'assigned') return 1; // driver assigned, still at Confirmed step
        if (current === 'order_updated') return 2; // For the custom timeline
        if (current === 'packed') return 2;
        if (current === 'picked_up' || current === 'out_for_delivery') return 3;
        if (current === 'delivered') return 4;
        return 0;
    };

    const formatScheduledTime = (timeStr, createdAt, status = '') => {
        if (!timeStr) return "";
        const slots = {
            '09:00 - 12:00': '09:00 AM - 12:00 PM',
            '13:00 - 16:00': '01:00 PM - 04:00 PM',
            '18:00 - 21:00': '06:00 PM - 09:00 PM'
        };
        if (slots[timeStr]) return slots[timeStr];

        const isTerminal = ['delivered', 'cancelled', 'returned', 'refund_completed', 'rejected', 'delivery_missed'].some(s => status?.toLowerCase().includes(s));
        const isActive = !isTerminal;

        const lower = timeStr.toLowerCase();
        if (lower === 'normal' && createdAt) {
            return `Express Delivery (ETA: ${dayjs(createdAt).add(120, 'minute').format('hh:mm A')})`;
        }
        if (lower === 'express' && createdAt) {
            return <DeliveryCountdown createdAt={createdAt} minutes={45} label="Express Delivery" isActive={isActive} />;
        }

        if (typeof timeStr === 'string' && timeStr.includes(':') && !timeStr.includes('-')) {
            try {
                return dayjs(`2024-01-01 ${timeStr}`).format("hh:mm A");
            } catch (e) { return timeStr; }
        }
        return timeStr;
    };


    // Terminal statuses that go into history/other sections
    const isReturnOrRefundStatus = (eff) => {
        return eff.startsWith('return_') || eff.startsWith('refund_') || eff === 'returned';
    };

    // Active Tracking: ALL orders that are not yet in a terminal state (not delivered, cancelled, or returned)
    const activeOrders = orders.filter(o => {
        const eff = getEffectiveStatus(o);
        if (eff === 'cancelled' || isReturnOrRefundStatus(eff)) return false;
        return eff !== 'delivered';
    });
    // Order History table: only successfully delivered orders (no active return)
    const historyOrders = orders.filter(o => {
        const eff = getEffectiveStatus(o);
        return eff === 'delivered' && (!o.returnStatus || o.returnStatus === 'none');
    });
    // Cancellations & Returns section
    const otherOrders = orders.filter(o => {
        const eff = getEffectiveStatus(o);
        return eff === 'cancelled' || isReturnOrRefundStatus(eff);
    });

    const columns = React.useMemo(() => [
        {
            title: t('image', 'Image'),
            render: (_, record) => {
                if (record.isSkeleton) return <Skeleton.Image active style={{ width: 50, height: 50 }} />;
                const activeItems = (record.items || []).filter(item => item.quantity > 0);
                const displayItems = activeItems.length > 0 ? activeItems : (record.items || []);
                const maxVisible = 1;
                const slicedItems = displayItems.slice(0, maxVisible);
                const hasMore = displayItems.length > maxVisible;
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {slicedItems.map((item, idx) => {
                            const src = item.product?.imageUrl || (item.product?.images?.length > 0 && item.product.images[0]) || item.imageUrl;
                            return (
                                <div
                                    key={idx}
                                    style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        overflow: 'hidden',
                                        backgroundColor: '#fff',
                                        flexShrink: 0
                                    }}
                                >
                                    <SafeImage
                                        src={src}
                                        fallbackSrc={item.imageUrl}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </div>
                            );
                        })}
                        {hasMore && (
                            <div
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '6px',
                                    backgroundColor: '#f1f5f9',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}
                            >
                                <span style={{ fontSize: '11px', fontWeight: 800, color: '#475569' }}>
                                    +{displayItems.length - maxVisible}
                                </span>
                            </div>
                        )}
                    </div>
                );
            },
            width: 120,
        },

        {
            title: t('order_id', 'Order ID'),
            dataIndex: 'orderId',
            key: 'orderId',
            render: (id, record) => {
                if (record.isSkeleton) return <Skeleton.Input active size="small" />;
                return <Text strong style={{ whiteSpace: 'nowrap' }}>#{id}</Text>;
            },
            width: 120,
        },

        {
            title: t('total_mrp', 'Total MRP'),
            key: 'totalMRP',
            render: (_, record) => {
                if (record.isSkeleton) return <Skeleton.Input active size="small" />;
                const itemsSubtotal = record.totalAmount || ((record.totalOrderAmount || 0) - (record.deliveryCharge ?? 50));
                const totalMRP = record.items?.reduce((sum, item) => sum + ((item.mrp || item.oldPrice || item.price) * item.quantity), 0) || itemsSubtotal;
                return (
                    <Text delete type="secondary" style={{ fontSize: 13 }}>
                        {formatPrice(totalMRP)}
                    </Text>
                );
            },
            width: 100,
        },
        {
            title: t('total_amount', 'Total Amount'),
            key: 'totalAmount',
            render: (_, record) => {
                if (record.isSkeleton) return <Skeleton.Input active size="small" />;
                return (
                    <Text strong className="pcu-color-purple">
                        {formatPrice(record.payableAmount !== undefined ? record.payableAmount : (record.totalOrderAmount || record.totalAmount))}
                    </Text>
                );
            },
            width: 120,
        },
        {
            title: t('status', 'Status'),
            key: 'status',
            render: (_, record) => {
                if (record.isSkeleton) return <Skeleton.Button active size="small" />;
                const s = getEffectiveStatus(record);
                const normalizedStatus = s?.toLowerCase() || '';
                const colorMap = {
                    delivered: 'green',
                    payment_pending: 'orange',
                    no_delivery_available: 'volcano',
                    cancelled: 'red',
                    confirmed: 'purple',
                    packed: 'blue',
                    picked_up: 'gold',
                    out_for_delivery: 'gold',
                    ordered: 'cyan',
                    order_updated: 'orange',
                    return_requested: 'orange',
                    return_approved: '#1890ff',
                    return_pickup_pending: 'volcano',
                    return_picked_up: 'cyan',
                    return_in_transit: 'blue',
                    return_delivered_to_seller: 'green',
                    return_rejected: 'red',
                    refund_processing: 'purple',
                    refund_completed: 'lime',
                    returned: 'lime',
                    awaiting_buyer_approval: 'warning',
                };
                const labelMap = {
                    delivered: t('delivered', 'Delivered'),
                    payment_pending: 'Payment Pending',
                    no_delivery_available: 'No Delivery Available',
                    cancelled: t('cancelled', 'Cancelled'),
                    confirmed: t('confirmed', 'Confirmed'),
                    packed: t('packed', 'Packed'),
                    picked_up: t('picked_up', 'Out for Delivery'),
                    out_for_delivery: t('picked_up', 'Out for Delivery'),
                    ordered: t('ordered', 'Ordered'),
                    order_updated: t('order_updated', 'Replace The Order'),
                    return_requested: t('return_requested', 'Return Requested'),
                    return_approved: t('return_approved', 'Return Approved'),
                    return_pickup_pending: t('return_pickup_pending', 'Return Pickup Pending'),
                    return_picked_up: t('return_picked_up', 'Return Picked Up'),
                    return_in_transit: t('return_in_transit', 'Return In Transit'),
                    return_delivered_to_seller: t('return_delivered_to_seller', 'Return Completed'),
                    return_rejected: t('return_rejected', 'Return Rejected'),
                    refund_processing: t('refund_processing', 'Refund Processing'),
                    refund_completed: t('refund_completed', 'Refund Completed'),
                    returned: t('returned', 'Returned'),
                    awaiting_buyer_approval: t('awaiting_buyer_approval', 'Action Required'),
                };
                const displayLabel = labelMap[normalizedStatus] || normalizedStatus.replace(/_/g, ' ');
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag color={colorMap[normalizedStatus] || 'default'} style={{ margin: 0 }}>
                            {displayLabel?.toUpperCase()}
                        </Tag>
                        {(s === 'awaiting_buyer_approval') && (
                            <Button
                                type="text"
                                danger
                                icon={<IconAlertCircle size={18} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIssueModalOrder(record);
                                }}
                                style={{ padding: 4, height: 'auto', display: 'flex', alignItems: 'center' }}
                            />
                        )}
                    </div>
                );
            },
            width: 180,
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => {
                if (record.isSkeleton) return <Skeleton.Button active size="small" />;
                return (
                    <Button
                        type="default"
                        size="small"
                        onClick={() => {
                            setSelectedOrder(record);
                            setIsModalOpen(true);
                        }}
                        style={{ borderRadius: 4, fontWeight: 500, color: '#003a8c', borderColor: '#d9d9d9' }}
                    >
                        View
                    </Button>
                );
            },
            width: 90,
            align: 'center',
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t, formatPrice, window.innerWidth, fetchOrders, showRescheduleConfirmation]);

    return (
        <div className="orders-page-container pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div style={{ marginTop: 16, background: "#fff", borderRadius: 16, border: "1px solid #f1f5f9", padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                        <div style={{
                            width: 48, height: 48, background: "#eef2ff", borderRadius: 12,
                            display: "flex", alignItems: "center", justifyContent: "center"
                        }}>
                            <AppstoreOutlined style={{ fontSize: 24, color: "#4f46e5" }} />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1e293b" }}>{t('my_orders', 'My Orders')}</h2>
                            <Text type="secondary" style={{ fontSize: 13, fontWeight: 500 }}>
                                {historyOrders.length + activeOrders.length + otherOrders.length} orders listed
                            </Text>
                        </div>
                    </div>

                    <div style={{ flex: 1, display: "flex", justifyContent: isMobile ? "flex-start" : "flex-end", gap: 16, overflowX: "auto", alignItems: "center" }}>
                        <Select value={filterCategory} onChange={handleCategoryChange} style={{ minWidth: 160 }}>
                            <Select.Option value="all">All Orders</Select.Option>
                            <Select.Option value="active">Active Orders</Select.Option>
                            <Select.Option value="delivered">Delivered Orders</Select.Option>
                            <Select.Option value="cancelled">Cancelled & Returns</Select.Option>
                        </Select>
                        <Select value={filterSpecificStatus} onChange={setFilterSpecificStatus} style={{ minWidth: 180 }}>
                            {getStatusOptions().map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                            ))}
                        </Select>
                        <Button
                            type="default"
                            onClick={() => { setFilterCategory("all"); setFilterSpecificStatus("all"); }}
                            style={{ borderRadius: 8, fontWeight: 500 }}
                        >
                            Reset
                        </Button>
                    </div>

                </div>

                <div className="pcu-border-top pcu-margin-x-n24 pcu-padding-x-24 pcu-padding-top-16">
                    <DataTable
                        columns={columns}
                        data={(() => {
                            if (loading && orders.length === 0) {
                                return Array.from({ length: 5 }).map((_, i) => ({ _id: `skel-${i}`, isSkeleton: true }));
                            }
                            const baseData = filterCategory === 'all' ? orders : filterCategory === 'active' ? activeOrders : filterCategory === 'delivered' ? historyOrders : otherOrders;
                            let filtered = filterSpecificStatus === 'all' ? baseData : baseData.filter(o => {
                                const eff = getEffectiveStatus(o);
                                if (filterSpecificStatus === 'picked_up' && (eff === 'picked_up' || eff === 'out_for_delivery')) return true;
                                return eff === filterSpecificStatus;
                            });
                            return [...filtered].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
                        })()}
                        rowKey="_id"
                        loading={false}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.limit,
                            total: pagination.total,
                            onChange: (page) => {
                                fetchOrders(page, true);
                                const pageContent = document.querySelector('.page-content');
                                if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                else window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                        }}
                        locale={{ emptyText: t('no_orders_found', 'No orders found.') }}
                        rowClassName={(record) => {
                            if (record.isSkeleton) return '';
                            const eff = getEffectiveStatus(record);
                            if (eff === 'ordered') return 'ordered-row-highlight';
                            if (eff === 'order_updated') return 'reordered-row-highlight';
                            return '';
                        }}
                    />
                </div>
            </div>

            <Modal
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                title={null}
                width={720}
                centered
                destroyOnClose
                bodyStyle={{ padding: 0, borderRadius: 16, overflow: "hidden" }}
                closeIcon={<></>}
            >
                {selectedOrder && (
                    <div style={{ background: "#f8fafc", position: "relative", display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
                        {/* Header Section */}
                        <div style={{ padding: "16px 20px", background: "#fff", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 16 }}>
                            <div style={{ width: 48, height: 48, background: "#eef2ff", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <AppstoreOutlined style={{ fontSize: 24, color: "#4f46e5" }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}>
                                    Order #{selectedOrder.orderId}
                                </h2>
                                <p style={{ margin: "2px 0 6px", fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                                    Customer: <span style={{ color: "#1e293b" }}>{selectedOrder.deliveryAddress?.name || `${selectedOrder.user?.firstName || ''} ${selectedOrder.user?.lastName || ''}`.trim()}</span>
                                </p>
                                <div style={{ display: "flex", gap: 6 }}>
                                    <Tag color={getStatusColor(selectedOrder.status)} style={{ margin: 0, borderRadius: 4, fontWeight: 800, fontSize: 10, padding: "0 8px", border: "1px solid " + (getStatusColor(selectedOrder.status) === 'default' ? '#e2e8f0' : getStatusColor(selectedOrder.status)), background: "#fff", color: getStatusColor(selectedOrder.status) === "default" ? "#64748b" : getStatusColor(selectedOrder.status) }}>
                                        {getStatusLabel(selectedOrder.status)?.toUpperCase()}
                                    </Tag>
                                    <Tag color="blue" style={{ margin: 0, borderRadius: 4, fontWeight: 800, fontSize: 10, padding: "0 8px", border: "1px solid #3b82f6", background: "#fff", color: "#3b82f6" }}>
                                        {selectedOrder.creditsUsed > 0 ? ((selectedOrder.codAmount === 0 && selectedOrder.payableAmount === 0) ? "CREDIT" : `CREDIT + ${(selectedOrder.paymentMethod || selectedOrder.paymentType || 'COD').toUpperCase()}`) : (selectedOrder.paymentMethod || selectedOrder.paymentType || 'COD').toUpperCase()}
                                    </Tag>
                                    {selectedOrder.paymentCollectionMethod && selectedOrder.paymentCollectionMethod !== selectedOrder.paymentMethod && (
                                        <Tag color="purple" style={{ margin: 0, borderRadius: 4, fontWeight: 800, fontSize: 10, padding: "0 8px", border: "1px solid #9333ea", background: "#fff", color: "#9333ea" }}>
                                            COLLECTED VIA {selectedOrder.paymentCollectionMethod.toUpperCase()}
                                        </Tag>
                                    )}
                                    {selectedOrder.paymentSubMethod && (
                                        <Tag color="cyan" style={{ margin: 0, borderRadius: 4, fontWeight: 800, fontSize: 10, padding: "0 8px", border: "1px solid #06b6d4", background: "#fff", color: "#06b6d4" }}>
                                            {selectedOrder.paymentSubMethod.toUpperCase()}
                                        </Tag>
                                    )}
                                </div>
                                {selectedOrder.transactionId && (
                                    <div style={{ marginTop: 4, fontSize: 11, color: "#64748b", fontWeight: 600 }}>
                                        {selectedOrder.transactionId.startsWith('NZN-') ? 'Ref ID:' : 'Txn ID:'} <span style={{ color: "#1e293b" }}>{selectedOrder.transactionId}</span>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                style={{
                                    position: "absolute", top: 16, right: 16, border: "none", background: "#fff",
                                    width: 32, height: 32, borderRadius: "50%", cursor: "pointer", color: "#94a3b8",
                                    display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                                }}
                            >
                                <CloseCircleOutlined style={{ fontSize: 18 }} />
                            </button>
                        </div>

                        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>

                            {selectedOrder.status === 'cancelled' || selectedOrder.status === 'return_requested' || selectedOrder.status === 'return_picked_up' || selectedOrder.status === 'returned' || selectedOrder.status === 'refund_completed' ? (
                                <Card className={selectedOrder.status === 'cancelled' ? "pcu-bg-red-light pcu-border-error pcu-margin-bottom-16" : (selectedOrder.status === 'refund_completed' ? "pcu-bg-green-light pcu-border-success pcu-margin-bottom-16" : "pcu-bg-orange-light pcu-border-orange pcu-margin-bottom-16")} style={{ borderLeft: '4px solid ' + (selectedOrder.status === 'cancelled' ? '#ef4444' : '#22c55e') }}>
                                    <Text type={selectedOrder.status === 'cancelled' ? "danger" : (selectedOrder.status === 'refund_completed' ? "success" : "warning")} strong style={{ fontSize: 15 }}>
                                        {selectedOrder.status === 'cancelled' ? <><CloseCircleOutlined /> Order Cancelled</> :
                                            selectedOrder.status === 'refund_completed' ? 'Refund Completed' : 'Return In Progress'}
                                    </Text>
                                    <p className="pcu-margin-top-4 pcu-margin-bottom-0 pcu-color-title">
                                        Current Status: <Tag color={getStatusColor(getEffectiveStatus(selectedOrder))} style={{ marginLeft: 4 }}>{getEffectiveStatus(selectedOrder)?.replace(/_/g, " ")?.toUpperCase()}</Tag>
                                    </p>
                                    {selectedOrder.returnRequest && selectedOrder.returnRequest.rejectionReason && (
                                        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6 }}>
                                            <Text type="danger" strong>Rejection Details ({selectedOrder.returnRequest.rejectionStage || 'System'}):</Text>
                                            <div style={{ fontSize: 13, color: '#cf1322', marginTop: 4 }}>
                                                {selectedOrder.returnRequest.rejectionReason}
                                            </div>
                                        </div>
                                    )}
                                    {selectedOrder.returnRequest && getEffectiveStatus(selectedOrder) === 'return_rejected' && !selectedOrder.returnRequest.rejectionReason && (selectedOrder.returnRequest.adminComment || selectedOrder.returnRequest.sellerComment) && (
                                        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6 }}>
                                            <Text type="danger" strong>Rejection Reason:</Text>
                                            <div style={{ fontSize: 13, color: '#cf1322', marginTop: 4 }}>
                                                {selectedOrder.returnRequest.adminComment && <div><strong>Admin:</strong> {selectedOrder.returnRequest.adminComment}</div>}
                                                {selectedOrder.returnRequest.sellerComment && !selectedOrder.returnRequest.sellerComment.includes('(Admin Action)') && <div><strong>Seller:</strong> {selectedOrder.returnRequest.sellerComment}</div>}
                                                {selectedOrder.returnRequest.sellerComment && selectedOrder.returnRequest.sellerComment.includes('(Admin Action)') && <div><strong>Admin:</strong> {selectedOrder.returnRequest.sellerComment.replace('(Admin Action)', '').trim()}</div>}
                                            </div>
                                        </div>
                                    )}
                                    {selectedOrder.statusHistory?.filter(h => h.status === selectedOrder.status).pop()?.comment && (
                                        <Text type="secondary" size="small" className="pcu-display-block pcu-margin-top-6">
                                            {t('update')}: {selectedOrder.statusHistory.filter(h => h.status === selectedOrder.status).pop().comment}
                                        </Text>
                                    )}
                                </Card>
                            ) : (
                                <div className="pcu-margin-bottom-24 pcu-padding-x-8 pcu-margin-top-8">
                                    <Steps
                                        current={getStatusStep(selectedOrder.status)}
                                        size="small"
                                        status={selectedOrder.status === 'cancelled' || selectedOrder.status === 'delivery_missed' ? 'error' : 'process'}
                                    >
                                        <Step title={t('ordered', 'Ordered')} />
                                        <Step title={t('confirmed', 'Confirmed')} />
                                        <Step title={t('packed', 'Packed')} />
                                        <Step title={t('picked_up', 'Out for Delivery')} />
                                        <Step title={t('delivered', 'Delivered')} />
                                    </Steps>
                                </div>
                            )}

                            {/* ── Delivery Partner Card ── */}
                            {selectedOrder.currentAssignedStaff && !['cancelled', 'delivered', 'delivery_missed', 'ordered', 'confirmed'].includes(selectedOrder.status?.toLowerCase()) && (
                                <div style={{
                                    margin: '0 0 20px',
                                    borderRadius: 14,
                                    overflow: 'hidden',
                                    border: '1px solid #e0e7ff',
                                    background: 'linear-gradient(135deg, #f5f3ff 0%, #eff6ff 100%)',
                                    boxShadow: '0 2px 12px rgba(79,70,229,0.07)'
                                }}>
                                    {/* Card Header */}
                                    <div style={{
                                        padding: '10px 16px',
                                        background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
                                        display: 'flex', alignItems: 'center', gap: 8
                                    }}>
                                        <IconTruck size={16} color="#fff" />
                                        <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: '0.8px', textTransform: 'uppercase' }}>Delivery Partner</span>
                                        <span style={{
                                            marginLeft: 'auto', fontSize: 10, fontWeight: 700,
                                            background: 'rgba(255,255,255,0.2)', color: '#fff',
                                            padding: '2px 8px', borderRadius: 20
                                        }}>
                                            {selectedOrder.status?.toLowerCase() === 'assigned' ? '🔵 En Route to Shop' :
                                                selectedOrder.status?.toLowerCase() === 'packed' ? '📦 Awaiting Pickup' :
                                                    selectedOrder.status?.toLowerCase() === 'picked_up' || selectedOrder.status?.toLowerCase() === 'out_for_delivery' ? '🚴 Out for Delivery' : '✅ Active'}
                                        </span>
                                    </div>
                                    {/* Card Body */}
                                    <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                                        {/* Avatar */}
                                        <div style={{
                                            width: 52, height: 52, borderRadius: '50%', overflow: 'hidden',
                                            border: '2px solid #4f46e5', flexShrink: 0, background: '#e0e7ff',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            {selectedOrder.currentAssignedStaff?.profilePhoto ? (
                                                <img
                                                    src={getImageUrl(selectedOrder.currentAssignedStaff.profilePhoto)}
                                                    alt="staff"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <span style={{ fontSize: 20 }}>🧑‍💼</span>
                                            )}
                                        </div>
                                        {/* Info */}
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                                <span style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>
                                                    {selectedOrder.currentAssignedStaff?.firstName} {selectedOrder.currentAssignedStaff?.lastName}
                                                </span>
                                                <span style={{
                                                    fontSize: 9, fontWeight: 700, background: '#dcfce7', color: '#166534',
                                                    padding: '1px 6px', borderRadius: 10, border: '1px solid #bbf7d0'
                                                }}>✓ VERIFIED</span>
                                            </div>
                                            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Delivery Executive</div>
                                            {selectedOrder.currentAssignedStaff?.phone && (
                                                <div style={{ fontSize: 12, color: '#4f46e5', fontWeight: 600, marginTop: 2 }}>
                                                    📞 {selectedOrder.currentAssignedStaff.phone}
                                                </div>
                                            )}
                                        </div>
                                        {/* Call Button */}
                                        {selectedOrder.currentAssignedStaff?.phone && (
                                            <a
                                                href={`tel:${selectedOrder.currentAssignedStaff.phone}`}
                                                style={{
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                                                    background: 'linear-gradient(135deg, #f97316, #ea580c)',
                                                    color: '#fff', padding: '8px 14px', borderRadius: 10,
                                                    textDecoration: 'none', boxShadow: '0 4px 12px rgba(249,115,22,0.3)',
                                                    fontSize: 11, fontWeight: 700, flexShrink: 0
                                                }}
                                            >
                                                <PhoneOutlined style={{ fontSize: 16 }} />
                                                Call
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {selectedOrder.status === 'delivery_missed' && (
                                <Card className="pcu-bg-orange-light pcu-border-orange pcu-margin-bottom-24" style={{ borderLeft: '4px solid #f97316' }}>
                                    <div className="pcu-flex pcu-gap-12">
                                        <IconAlertCircle size={24} color="#f97316" />
                                        <div>
                                            <Text strong className="pcu-font-16 pcu-color-orange pcu-display-block">{t('delivery_missed_title')}</Text>
                                            <Text className="pcu-color-orange">
                                                {t('delivery_missed_text')}
                                            </Text>
                                            <div className="pcu-margin-top-12">
                                                <Button
                                                    size="small"
                                                    type="primary"
                                                    icon={<IconCalendarStats size={16} />}
                                                    onClick={() => showRescheduleConfirmation(selectedOrder)}
                                                    className="pcu-bg-orange pcu-margin-right-8"
                                                >
                                                    {t('reschedule_now')}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    danger
                                                    onClick={showCancelConfirmation}
                                                >
                                                    {t('cancel_refund')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            )}

                            {selectedOrder.status === 'AWAITING_BUYER_APPROVAL' && selectedOrder.productIssueRequest && (
                                <Card className="pcu-bg-orange-light pcu-border-orange pcu-margin-bottom-24" style={{ borderLeft: '4px solid #f97316' }}>
                                    <div className="pcu-flex pcu-gap-12">
                                        <IconAlertCircle size={24} color="#f97316" />
                                        <div style={{ flex: 1 }}>
                                            <Text strong style={{ fontSize: 16, color: '#9a3412', display: 'block' }}>Action Required: Order Update</Text>
                                            <Text style={{ color: '#7c2d12' }}>
                                                The seller has reported issues with some items in your order. Please review the changes below:
                                            </Text>
                                            <div className="pcu-margin-top-12 pcu-bg-white pcu-radius-8 pcu-padding-12 pcu-border">
                                                {selectedOrder.productIssueRequest.items?.map((issue, idx) => {
                                                    const originalItem = selectedOrder.items?.find(item => String(item.product?._id || item.product) === String(issue.product));
                                                    return (
                                                        <div key={idx} className="pcu-flex pcu-justify-between pcu-items-center pcu-margin-bottom-8">
                                                            <div>
                                                                <Text strong>{originalItem?.name || 'Product'}</Text>
                                                                <div className="pcu-font-11 pcu-color-muted">
                                                                    {issue.type === 'REMOVE' ? <Tag color="red">REMOVE</Tag> : <Tag color="orange">QTY: {issue.newValue}</Tag>}
                                                                    Reason: {issue.reason}
                                                                </div>
                                                            </div>
                                                            <Text delete type="secondary">{formatPrice(originalItem?.price * originalItem?.quantity)}</Text>
                                                        </div>
                                                    );
                                                })}
                                                <Divider className="pcu-margin-y-8" />
                                                <div className="pcu-flex-between pcu-font-14">
                                                    <Text strong>Revised Total Payable:</Text>
                                                    <Text strong className="pcu-color-purple pcu-font-18">{formatPrice(selectedOrder.productIssueRequest.newTotals?.payableAmount)}</Text>
                                                </div>
                                                <div className="pcu-font-11 pcu-color-success pcu-margin-top-4">
                                                    * Difference of {formatPrice(selectedOrder.payableAmount - selectedOrder.productIssueRequest.newTotals?.payableAmount)} will be refunded to your Nearzon Wallet.
                                                </div>
                                            </div>
                                            <div className="pcu-margin-top-16 pcu-flex pcu-gap-10">
                                                <Button
                                                    type="primary"
                                                    onClick={() => handleProductIssueRespond('approved')}
                                                    loading={actionLoading}
                                                    className="pcu-bg-orange pcu-border-none"
                                                >
                                                    Accept Changes
                                                </Button>
                                                <Button
                                                    danger
                                                    onClick={() => handleProductIssueRespond('rejected')}
                                                    loading={actionLoading}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            )}

                            {selectedOrder.deliveryTimeChangeRequest && selectedOrder.deliveryTimeChangeRequest.status === 'pending' && (
                                <Card className="pcu-bg-blue-light pcu-border-slate pcu-margin-bottom-24" style={{ borderLeft: '4px solid #0ea5e9' }}>
                                    <div className="pcu-flex pcu-gap-12">
                                        <IconClock size={24} color="#0ea5e9" />
                                        <div style={{ flex: 1 }}>
                                            <Text strong style={{ fontSize: 16, color: '#0369a1', display: 'block' }}>{t('delivery_time_change_requested')}</Text>
                                            <Text style={{ color: '#0c4a6e' }}>
                                                {t('seller_requested_time_change')}
                                                <br />
                                                <Text strong>{formatDate(selectedOrder.deliveryTimeChangeRequest.requestedDate)}</Text> at <Text strong>{formatScheduledTime(selectedOrder.deliveryTimeChangeRequest.requestedTime)}</Text>
                                            </Text>
                                            <div className="pcu-margin-top-12 pcu-flex pcu-gap-10">
                                                <Button
                                                    size="small"
                                                    type="primary"
                                                    onClick={() => handleTimeChangeRespond('approved')}
                                                    loading={actionLoading}
                                                    className="pcu-bg-blue"
                                                >
                                                    {t('approve')}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    danger
                                                    onClick={() => handleTimeChangeRespond('rejected')}
                                                    loading={actionLoading}
                                                >
                                                    {t('reject')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            )}



                            {/* Consolidated Customer & Delivery Card */}
                            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ width: 4, height: 14, background: "#10b981", borderRadius: 2 }}></div>
                                Identity & Delivery
                            </div>

                            <div style={{
                                background: "#fff", borderRadius: 12, border: "1px solid #f1f5f9",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.03)", marginBottom: 16, overflow: "hidden"
                            }}>
                                <div style={{ padding: "12px 16px", display: "grid", gridTemplateColumns: "1.2fr 1.5fr 1fr", gap: 12 }}>
                                    <div>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 }}>Customer</div>
                                        <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b" }}>{selectedOrder.deliveryAddress?.name || `${selectedOrder.user?.firstName || ''} ${selectedOrder.user?.lastName || ''}`.trim()}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 }}>Contact</div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <PhoneOutlined style={{ fontSize: 12, color: "#10b981" }} />
                                                <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>{selectedOrder.deliveryAddress?.phone || selectedOrder.user?.phone || "—"}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 }}>Ordered</div>
                                        <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b" }}>{dayjs(selectedOrder.createdAt).format("DD MMM YYYY")}</div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginTop: 2 }}>{dayjs(selectedOrder.createdAt).format("hh:mm A")}</div>
                                    </div>
                                </div>

                                <div style={{ height: 1, background: "#f8fafc", margin: "0 16px" }}></div>

                                <div style={{ padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr 2.5fr", gap: 12 }}>
                                    <div>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 }}>
                                            {selectedOrder.status === 'delivered' ? 'Delivered At' : 'Slot'}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            {selectedOrder.status === 'delivered' ? (
                                                <>
                                                    <div style={{ fontSize: 13, fontWeight: 800, color: "#16a34a" }}>
                                                        {dayjs(selectedOrder.deliveredAt || selectedOrder.updatedAt).format("DD MMM YYYY")}
                                                    </div>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: "#15803d" }}>
                                                        {dayjs(selectedOrder.deliveredAt || selectedOrder.updatedAt).format("hh:mm A")}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b" }}>
                                                        {selectedOrder.scheduledDate ? formatDate(selectedOrder.scheduledDate) : "Today"}
                                                    </div>
                                                    <div style={{ fontSize: 11, fontWeight: 600, color: "#ea580c" }}>
                                                        {selectedOrder.scheduledTime ? formatScheduledTime(selectedOrder.scheduledTime, selectedOrder.createdAt, selectedOrder.status) : <DeliveryCountdown createdAt={selectedOrder.createdAt} minutes={45} label="Express Delivery" isActive={!['delivered', 'cancelled', 'returned'].some(s => selectedOrder.status?.toLowerCase().includes(s))} />}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 }}>Address</div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", lineHeight: 1.4 }}>
                                            {selectedOrder.deliveryAddress?.street}, {selectedOrder.deliveryAddress?.city}, {selectedOrder.deliveryAddress?.taluk}, {selectedOrder.deliveryAddress?.district}, {selectedOrder.deliveryAddress?.pincode}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Shop Details Card */}
                            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ width: 4, height: 14, background: "#f59e0b", borderRadius: 2 }}></div>
                                Shop Details
                            </div>
                            <div style={{
                                background: "#fff", borderRadius: 12, border: "1px solid #f1f5f9",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.03)", marginBottom: 16, padding: "12px 16px"
                            }}>
                                {(() => {
                                    const shop = selectedOrder.shop || {};

                                    // Mask Name
                                    const rawName = shop.name || "Nearzon Seller";
                                    let maskedName = rawName;
                                    if (rawName !== "Nearzon Seller" && rawName.length > 2) {
                                        const firstPart = rawName.slice(0, 3);
                                        maskedName = `${firstPart}${"*".repeat(rawName.length - 3)}`;
                                    }

                                    // Mask Phone
                                    const rawPhone = shop.phone || "";
                                    let maskedPhone = "";
                                    if (rawPhone) {
                                        const cleanPhone = rawPhone.toString().trim();
                                        if (cleanPhone.length > 4) {
                                            maskedPhone = cleanPhone.slice(0, 2) + "######" + cleanPhone.slice(-2);
                                        } else {
                                            maskedPhone = "######";
                                        }
                                    }

                                    // Mask Email
                                    const rawEmail = shop.email || "";
                                    let maskedEmail = "";
                                    if (rawEmail) {
                                        const cleanEmail = rawEmail.toString().trim();
                                        const atIdx = cleanEmail.indexOf('@');
                                        if (atIdx !== -1) {
                                            const local = cleanEmail.slice(0, atIdx);
                                            const domain = cleanEmail.slice(atIdx);
                                            const maskedLocal = local.length > 2 ? local.slice(0, 2) + "*****" : "*****";
                                            maskedEmail = `${maskedLocal}${domain}`;
                                        } else {
                                            maskedEmail = "*****";
                                        }
                                    }

                                    // Mask Address
                                    const rawAddress = shop.address || "";
                                    let maskedAddress = "";
                                    if (rawAddress) {
                                        const cleanAddr = rawAddress.toString().trim();
                                        maskedAddress = cleanAddr.length > 8 ? cleanAddr.slice(0, 8) + "********************" : "********************";
                                    }

                                    return (
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
                                            <div style={{ flex: 1 }}>
                                                <span style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", display: "block", marginBottom: 6 }}>{maskedName}</span>
                                                {rawAddress && (
                                                    <div style={{ fontSize: 12, fontWeight: 500, color: "#64748b", lineHeight: 1.4, marginBottom: 8 }}>
                                                        {maskedAddress}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ flex: "0 0 auto", minWidth: 150 }}>
                                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                                    {rawPhone && (
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                            <PhoneOutlined style={{ fontSize: 12, color: "#10b981" }} />
                                                            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>{maskedPhone}</span>
                                                        </div>
                                                    )}
                                                    {rawEmail && (
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                            <MailOutlined style={{ fontSize: 12, color: "#10b981" }} />
                                                            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", wordBreak: "break-all" }}>{maskedEmail}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Items Section */}
                            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ width: 4, height: 14, background: "#4f46e5", borderRadius: 2 }}></div>
                                Items
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                                {(() => {
                                    const itemsWithIssues = [];
                                    const itemsWithoutIssues = [];
                                    selectedOrder.items?.forEach((item) => {
                                        const isIssue = (selectedOrder.status === 'AWAITING_BUYER_APPROVAL' && selectedOrder.productIssueRequest?.status === 'PENDING')
                                            ? selectedOrder.productIssueRequest.items?.some(i => String(i.product?._id || i.product) === String(item.product?._id || item.product))
                                            : false;
                                        if (isIssue) {
                                            itemsWithIssues.push(item);
                                        } else {
                                            itemsWithoutIssues.push(item);
                                        }
                                    });
                                    const sortedItems = [...itemsWithIssues, ...itemsWithoutIssues];

                                    return sortedItems.map((item, idx) => {
                                        const isItemCancelled = item.quantity === 0 || item.status === 'Cancelled';
                                        const displayPrice = isItemCancelled ? 0 : (item.price * (item.quantity || 1));
                                        const itemOpacity = isItemCancelled ? 0.5 : 1;
                                        const textDecoration = isItemCancelled ? "line-through" : "none";

                                        const activeIssue = (selectedOrder.status === 'AWAITING_BUYER_APPROVAL' && selectedOrder.productIssueRequest?.status === 'PENDING')
                                            ? selectedOrder.productIssueRequest.items?.find(i => String(i.product?._id || i.product) === String(item.product?._id || item.product))
                                            : null;
                                        const hasIssue = !!activeIssue;

                                        return (
                                            <div key={idx} style={{
                                                display: "flex", flexDirection: "column", gap: 12, padding: "16px",
                                                border: hasIssue ? "1px solid #fca5a5" : "none",
                                                borderBottom: hasIssue ? "1px solid #fca5a5" : "1px solid #f1f5f9",
                                                borderRadius: hasIssue ? "12px" : "0px",
                                                backgroundColor: hasIssue ? "#fff5f5" : "transparent",
                                                alignItems: "stretch",
                                                opacity: itemOpacity
                                            }}>
                                                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                                                    <div style={{ width: 64, height: 64, borderRadius: 12, overflow: "hidden", border: "1px solid #f1f5f9", flexShrink: 0 }}>
                                                        <SafeImage src={item.product?.imageUrl || (item.product?.images && item.product.images[0]) || item.imageUrl} fallbackSrc={item.imageUrl} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 800, fontSize: 15, color: "#1e293b", marginBottom: 4, textDecoration }}>{item.name || item.product?.name}</div>
                                                        {isItemCancelled && (
                                                            <div style={{ color: "#ef4444", fontSize: 11, fontWeight: "bold", background: "#fef2f2", padding: "2px 6px", borderRadius: 4, display: "inline-block", marginTop: 2, marginBottom: 4 }}>
                                                                Removed / Cancelled
                                                            </div>
                                                        )}
                                                        <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600 }}>
                                                            Weight: {(() => {
                                                                let opts = item.selectedOptions || item.options;
                                                                if (typeof opts === 'string' && opts.startsWith('{')) {
                                                                    try { opts = JSON.parse(opts); } catch (e) { }
                                                                }
                                                                if (opts && typeof opts === 'object' && !Array.isArray(opts) && Object.keys(opts).length > 0) {
                                                                    return Object.entries(opts).map(([k, v]) => localFormatUnit(v, k)).join(', ');
                                                                }
                                                                return item.variantName || item.unit || item.weight || item.product?.unit || item.product?.weight || "1 Unit";
                                                            })()}
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: "right" }}>
                                                        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 8 }}>
                                                            <span style={{ fontSize: 12, color: "#94a3b8", textDecoration: "line-through", fontWeight: 500 }}>
                                                                ₹{(item.mrp || item.oldPrice || item.price).toFixed(2)}
                                                            </span>
                                                            <span style={{ fontWeight: 900, fontSize: 16, color: "#1e293b", textDecoration }}>
                                                                ₹{displayPrice.toFixed(2)}
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 700 }}>Qty: {item.quantity}</div>
                                                    </div>
                                                </div>

                                                {hasIssue && (
                                                    <div style={{
                                                        display: "flex", alignItems: "center", gap: 8,
                                                        backgroundColor: "#fee2e2", padding: "8px 12px",
                                                        borderRadius: "8px", border: "1px solid #fca5a5",
                                                        marginTop: "4px"
                                                    }}>
                                                        <span style={{ fontSize: "14px" }}>⚠️</span>
                                                        <div style={{ fontSize: "12px", color: "#991b1b", fontWeight: 600 }}>
                                                            <span style={{ textTransform: "uppercase", fontWeight: 800 }}>{activeIssue.type} REQUESTED</span>
                                                            {activeIssue.type === 'UPDATE_QTY' && ` (New Qty: ${activeIssue.newValue})`}
                                                            <div style={{ fontWeight: 500, color: "#b91c1c", marginTop: "2px" }}>
                                                                Reason: {activeIssue.reason || "N/A"}
                                                                {activeIssue.customReason && ` - ${activeIssue.customReason}`}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>

                            {/* Summary Card */}
                            {(() => {
                                const itemsSubtotal = selectedOrder.items?.reduce((sum, item) => {
                                    const qty = (item.status === 'Cancelled' || item.quantity === 0) ? 0 : (item.quantity !== undefined ? item.quantity : 1);
                                    return sum + ((item.price || item.mrp || 0) * qty);
                                }, 0) || ((selectedOrder.totalOrderAmount || selectedOrder.totalAmount || 0) - (selectedOrder.deliveryCharge ?? 50));
                                const totalMRP = selectedOrder.items?.reduce((sum, item) => {
                                    const qty = (item.status === 'Cancelled' || item.quantity === 0) ? 0 : (item.quantity !== undefined ? item.quantity : 1);
                                    return sum + ((item.mrp || item.oldPrice || item.price || 0) * qty);
                                }, 0) || itemsSubtotal;
                                const productDiscount = Math.max(0, totalMRP - itemsSubtotal);
                                const totalSavings = productDiscount + (selectedOrder.deliverySavings || 0) + (selectedOrder.couponDiscount || 0);

                                return (
                                    <div style={{ background: "#f8fafc", borderRadius: 12, padding: "14px 16px", border: "1px solid #f1f5f9" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Total MRP</span>
                                            <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 700 }}>₹{totalMRP.toFixed(2)}</span>
                                        </div>
                                        {productDiscount > 0 && (
                                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                                <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>Product Discount</span>
                                                <span style={{ fontSize: 13, color: "#16a34a", fontWeight: 700 }}>-₹{productDiscount.toFixed(2)}</span>
                                            </div>
                                        )}

                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Items Subtotal</span>
                                            <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 700 }}>₹{itemsSubtotal.toFixed(2)}</span>
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Delivery Charges</span>
                                            <span style={{ fontSize: 13, color: (selectedOrder.deliveryCharge ?? 50) === 0 ? "#ea580c" : "#16a34a", fontWeight: 700 }}>
                                                {(selectedOrder.deliveryCharge ?? 50) === 0 ? 'FREE' : `+₹${(selectedOrder.deliveryCharge ?? 50).toFixed(2)}`}
                                            </span>
                                        </div>
                                        {!!selectedOrder.deliverySavings && (
                                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                                <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>Delivery Discount</span>
                                                <span style={{ fontSize: 13, color: "#16a34a", fontWeight: 700 }}>-₹{selectedOrder.deliverySavings.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div style={{ height: 1, background: "#e2e8f0", margin: "10px 0" }}></div>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 800 }}>Amount Paid</span>
                                            <span style={{ fontSize: 20, color: "#000000ff", fontWeight: 900 }}>₹{(selectedOrder.payableAmount !== undefined ? selectedOrder.payableAmount : selectedOrder.codAmount)?.toFixed(2)}</span>
                                        </div>
                                        {totalSavings > 0 && (
                                            <div style={{ marginTop: 12, padding: "8px 0", borderRadius: 8, background: "#dcfce7", textAlign: "center" }}>
                                                <span style={{ fontSize: 12, fontWeight: 800, color: "#16a34a", letterSpacing: 0.5 }}>
                                                    YOU SAVED ₹{totalSavings.toFixed(2)} ON THIS ORDER! 🎉
                                                </span>
                                            </div>
                                        )}
                                        {(selectedOrder.status?.includes('return') || selectedOrder.status?.includes('refund') || (selectedOrder.returnStatus && selectedOrder.returnStatus !== 'none')) && (
                                            <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", gap: "8px", alignItems: "flex-start" }}>
                                                <InfoCircleOutlined style={{ color: "#3b82f6", marginTop: "2px", fontSize: 14 }} />
                                                <span style={{ fontSize: 12, fontWeight: 600, color: "#1e3a8a", lineHeight: 1.4 }}>
                                                    {t('refund_info_text', 'The refund amount will be credited to your original payment method within 7 business days after the return is verified.')}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Footer Section */}
                        <div style={{ padding: "14px 20px", display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: "1px solid #f1f5f9", background: "#fff", gap: 12 }}>
                            {(() => {
                                const effectiveStatus = getEffectiveStatus(selectedOrder)?.toLowerCase() || '';
                                const isPostDelivery = effectiveStatus === 'delivered' || effectiveStatus === 'returned' || effectiveStatus.startsWith('return_') || effectiveStatus.startsWith('refund_');
                                if (!isPostDelivery) return null;
                                return (
                                    <Button icon={<DownloadOutlined />} onClick={() => handleDownloadInvoice(selectedOrder?._id)} style={{ borderRadius: 8, fontWeight: 600 }}>
                                        {t('download_invoice', 'Invoice')}
                                    </Button>
                                );
                            })()}

                            {(() => {
                                const buttons = [];
                                const effectiveStatus = getEffectiveStatus(selectedOrder);
                                if (effectiveStatus === 'placed' || effectiveStatus === 'confirmed' || effectiveStatus === 'ordered' || effectiveStatus === 'packed' || effectiveStatus === 'processing' || effectiveStatus === 'delivery_missed') {
                                    buttons.push(
                                        <Button key="cancel" danger onClick={showCancelConfirmation} style={{ borderRadius: 8, fontWeight: 700 }}>
                                            {effectiveStatus === 'delivery_missed' ? t('cancel_refund_btn') : t('cancel_order_btn')}
                                        </Button>
                                    );
                                }
                                if (effectiveStatus === 'delivery_missed') {
                                    buttons.push(
                                        <Button key="reschedule" type="primary" onClick={() => showRescheduleConfirmation(selectedOrder)} style={{ borderRadius: 8, fontWeight: 700, background: "#8b5cf6" }}>
                                            {t('reschedule_delivery_btn')}
                                        </Button>
                                    );
                                }
                                if (effectiveStatus === 'delivered' && (!selectedOrder?.returnStatus || selectedOrder?.returnStatus === 'none')) {
                                    const deliveredDate = selectedOrder?.deliveredAt || selectedOrder?.updatedAt;
                                    const hasReturnableItems = selectedOrder?.items?.some(item =>
                                        item.allowReturn !== false && item.product?.categoryId?.allowReturn !== false
                                    );

                                    if (deliveredDate && hasReturnableItems) {
                                        const expiryTime = new Date(deliveredDate).getTime() + 7 * 24 * 60 * 60 * 1000;
                                        const timeLeftMs = expiryTime - Date.now();
                                        if (timeLeftMs > 0) {

                                            buttons.push(
                                                <Button key="return" onClick={showReturnConfirmation} style={{ borderRadius: 8, fontWeight: 700, background: "#f97316", color: "#fff", border: "none" }}>
                                                    {t('return_order_btn') || 'Return Order'}
                                                </Button>
                                            );
                                        }
                                    }
                                }
                                if (effectiveStatus === 'cancelled' || effectiveStatus === 'returned' || effectiveStatus === 'refund_completed') {
                                    buttons.push(
                                        <Button key="reorder" type="primary" onClick={handleReorder} style={{ borderRadius: 8, fontWeight: 700, background: "#10b981", border: "none" }}>
                                            {t('reorder_btn')}
                                        </Button>
                                    );
                                }
                                return buttons;
                            })()}

                            <Button onClick={() => setIsModalOpen(false)} style={{ borderRadius: 8, fontWeight: 700, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#64748b" }}>
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>


            <Modal
                title={t('cancel_order_btn')}
                open={cancelModalOpen}
                onCancel={() => setCancelModalOpen(false)}
                onOk={handleCancelOrder}
                okText={t('confirm_cancel')}
                okType="danger"
                cancelText={t('stay_here')}
                confirmLoading={actionLoading}
            >
                <p className="pcu-margin-bottom-10">{t('confirm_cancel_msg')}</p>

                <div className="pcu-margin-bottom-12">
                    <Text strong className="pcu-display-block pcu-margin-bottom-8">{t('reason_for_cancellation')}:</Text>
                    <Radio.Group
                        onChange={(e) => {
                            const val = e.target.value;
                            setCancellationReasonOption(val);
                            if (val !== 'Other') {
                                setCancelReason(val);
                            } else {
                                setCancelReason("");
                            }
                        }}
                        value={cancellationReasonOption}
                        className="pcu-flex pcu-flex-column pcu-gap-8"
                    >
                        <Radio value="Ordered by mistake">{t('ordered_by_mistake')}</Radio>
                        <Radio value="Found a better price">{t('found_better_price')}</Radio>
                        <Radio value="Delivery time is too long">{t('delivery_too_long')}</Radio>
                        <Radio value="Changed my mind">{t('changed_my_mind')}</Radio>
                        <Radio value="Other">{t('other')}</Radio>
                    </Radio.Group>
                </div>

                {cancellationReasonOption === 'Other' && (
                    <Input.TextArea
                        placeholder={t('reason')}
                        rows={3}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                    />
                )}
            </Modal>

            <Modal
                title={null}
                open={returnModalOpen}
                onCancel={() => setReturnModalOpen(false)}
                footer={null}
                width={720}
                centered
                getContainer={() => document.body}
                styles={{ body: { padding: 0 }, content: { borderRadius: 20, overflow: 'hidden', padding: 0 } }}
            >
                {/* Premium Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    padding: '28px 32px 24px',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,129,16,0.15)' }} />
                    <div style={{ position: 'absolute', bottom: -20, left: '40%', width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,129,16,0.2)', border: '1px solid rgba(255,129,16,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                            📦
                        </div>
                        <div>
                            <div style={{ color: '#fff', fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }}>Return Items</div>
                            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginTop: 2 }}>Select the items you want to return</div>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: '16px 20px', maxHeight: '60vh', overflowY: 'auto', background: '#f8f9fc' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {selectedOrder?.items
                            ?.map((item, originalIdx) => ({ item, originalIdx }))
                            .filter(({ item }) => item.quantity > 0 && item.status !== 'Cancelled')
                            .map(({ item, originalIdx }) => {
                                const idx = originalIdx; // use original index for returnItemData keys
                                const itemData = returnItemData[idx] || {};
                                const isSelected = itemData.selected;
                                const isReturnable = item.allowReturn !== false && item.product?.categoryId?.allowReturn !== false;

                                return (
                                    <div
                                        key={idx}
                                        onClick={() => {
                                            if (isReturnable && !isSelected) {
                                                setReturnItemData(prev => ({
                                                    ...prev,
                                                    [idx]: {
                                                        selected: true,
                                                        reason: prev[idx]?.reason || "",
                                                        description: prev[idx]?.description || "",
                                                        quantity: prev[idx]?.quantity || item.quantity || 1,
                                                        images: prev[idx]?.images || [],
                                                        previews: prev[idx]?.previews || []
                                                    }
                                                }));
                                            }
                                        }}
                                        style={{
                                            borderRadius: 10,
                                            border: isSelected ? '2px solid #ff8110' : '1.5px solid #e8ecf0',
                                            background: isReturnable ? '#fff' : '#fafafa',
                                            boxShadow: isSelected ? '0 2px 12px rgba(255,129,16,0.12)' : '0 1px 3px rgba(0,0,0,0.05)',
                                            transition: 'all 0.2s ease',
                                            overflow: 'hidden',
                                            cursor: isReturnable ? (isSelected ? 'default' : 'pointer') : 'not-allowed',
                                            opacity: isReturnable ? 1 : 0.6
                                        }}
                                    >
                                        <div style={{ display: 'flex', gap: 10, padding: '8px 10px' }}>
                                            {/* Left Column: Product details and Upload Evidence */}
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                                {/* Product Row */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 36, width: 20, flexShrink: 0 }}>
                                                        <Checkbox
                                                            checked={!!isSelected}
                                                            disabled={!isReturnable}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                if (!isReturnable) return;
                                                                setReturnItemData(prev => ({
                                                                    ...prev,
                                                                    [idx]: {
                                                                        selected: e.target.checked,
                                                                        reason: prev[idx]?.reason || "",
                                                                        description: prev[idx]?.description || "",
                                                                        quantity: prev[idx]?.quantity || item.quantity || 1,
                                                                        images: prev[idx]?.images || [],
                                                                        previews: prev[idx]?.previews || []
                                                                    }
                                                                }));
                                                            }}
                                                            style={{ '--ant-color-primary': '#ff8110' }}
                                                        />
                                                    </div>
                                                    <div style={{ width: 36, height: 36, borderRadius: 6, overflow: 'hidden', border: '1px solid #f0f0f0', flexShrink: 0 }}>
                                                        <img
                                                            src={getImageUrl(item.imageUrl || item.product?.images?.[0] || item.product?.image)}
                                                            alt={item.name}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            onError={(e) => { e.target.src = 'https://via.placeholder.com/36?text=?'; }}
                                                        />
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 36 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <div style={{ fontWeight: 600, fontSize: 12, color: '#1a1a2e', lineHeight: 1.2 }}>{item.name}</div>
                                                            {!isReturnable && <Tag color="error" style={{ fontSize: 9, lineHeight: '14px', padding: '0 4px', margin: 0, border: 'none' }}>Non-returnable</Tag>}
                                                        </div>
                                                        <div style={{ marginTop: 1, display: 'flex', gap: 6, alignItems: 'center' }}>
                                                            <span style={{ fontSize: 9, color: '#888', background: '#f5f5f5', padding: '1px 5px', borderRadius: 20 }}>Qty: {item.quantity}</span>
                                                            <span style={{ fontSize: 10, fontWeight: 600, color: '#ff8110' }}>₹{item.price}</span>
                                                        </div>
                                                        {isSelected && (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }} onClick={(e) => e.stopPropagation()}>
                                                                <span style={{ fontSize: 10, fontWeight: 600, color: '#555' }}>Return Qty:</span>
                                                                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d9d9d9', borderRadius: 4, overflow: 'hidden', background: '#fff' }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const currentQty = returnItemData[idx]?.quantity || 1;
                                                                            if (currentQty > 1) {
                                                                                setReturnItemData(prev => ({
                                                                                    ...prev,
                                                                                    [idx]: { ...prev[idx], quantity: currentQty - 1 }
                                                                                }));
                                                                            }
                                                                        }}
                                                                        style={{ border: 'none', background: '#f5f5f5', padding: '1px 6px', cursor: 'pointer', fontWeight: 'bold', fontSize: 11 }}
                                                                    >
                                                                        -
                                                                    </button>
                                                                    <span style={{ padding: '1px 6px', fontSize: 10, fontWeight: 600, minWidth: 16, textAlign: 'center' }}>
                                                                        {itemData.quantity || 1}
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const currentQty = returnItemData[idx]?.quantity || 1;
                                                                            if (currentQty < item.quantity) {
                                                                                setReturnItemData(prev => ({
                                                                                    ...prev,
                                                                                    [idx]: { ...prev[idx], quantity: currentQty + 1 }
                                                                                }));
                                                                            }
                                                                        }}
                                                                        style={{ border: 'none', background: '#f5f5f5', padding: '1px 6px', cursor: 'pointer', fontWeight: 'bold', fontSize: 11 }}
                                                                    >
                                                                        +
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Upload Evidence (only if selected & reason chosen) */}
                                                {isSelected && itemData.reason && (
                                                    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 7, marginTop: 2 }}>
                                                        <div style={{ fontSize: 10, fontWeight: 700, color: returnValidationErrors[idx]?.images ? 'red' : '#555', letterSpacing: 0.4, marginBottom: 5, textTransform: 'uppercase' }}>
                                                            Upload Evidence <span style={{ fontWeight: 400, color: '#aaa', textTransform: 'none', letterSpacing: 0 }}>({(itemData.images?.length || 0)}/3)</span> <span style={{ fontSize: 9, fontWeight: 500, color: '#9ca3af', marginLeft: 6, textTransform: 'none' }}>• Max {maxUploadSizeMB}MB</span>
                                                        </div>
                                                        {returnValidationErrors[idx]?.images && (
                                                            <div style={{ color: 'red', fontSize: 10, marginBottom: 4 }}>{returnValidationErrors[idx].images}</div>
                                                        )}
                                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                                            {itemData.previews?.map((preview, imgIdx) => (
                                                                <div key={imgIdx} style={{ position: 'relative', width: 44, height: 44, borderRadius: 6, overflow: 'hidden', border: '1px solid #e8ecf0', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                                                                    <img src={preview} alt={`preview-${imgIdx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                    <div
                                                                        onClick={() => removeItemImage(idx, imgIdx)}
                                                                        style={{
                                                                            position: 'absolute', top: 0, right: 0,
                                                                            background: 'linear-gradient(135deg, #ff5252, #c62828)',
                                                                            width: 15, height: 15,
                                                                            display: 'flex', justifyContent: 'center', alignItems: 'center',
                                                                            cursor: 'pointer', borderBottomLeftRadius: 5
                                                                        }}
                                                                    >
                                                                        <CloseCircleOutlined style={{ color: '#fff', fontSize: 8 }} />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            {(itemData.images?.length || 0) < 3 && (
                                                                <div style={{
                                                                    position: 'relative', width: 44, height: 44, borderRadius: 6,
                                                                    border: '1px dashed #ff8110', display: 'flex', flexDirection: 'column',
                                                                    justifyContent: 'center', alignItems: 'center', cursor: 'pointer',
                                                                    background: 'rgba(255,129,16,0.04)', gap: 1, transition: 'all 0.2s'
                                                                }}>
                                                                    <span style={{ fontSize: 15, color: '#ff8110', lineHeight: 1 }}>+</span>
                                                                    <span style={{ fontSize: 7, color: '#ff8110', fontWeight: 600 }}>PHOTO</span>
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        multiple
                                                                        onChange={(e) => handleItemImageUpload(idx, e)}
                                                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Right Column: Return Reason and Description */}
                                            {isSelected && (
                                                <div style={{ width: 230, display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                    {/* Select dropdown */}
                                                    <div>
                                                        <Select
                                                            style={{ width: '100%' }}
                                                            status={returnValidationErrors[idx]?.reason ? 'error' : ''}
                                                            size="middle"
                                                            placeholder="Select a reason..."
                                                            value={itemData.reason || undefined}
                                                            onChange={(val) => {
                                                                setReturnItemData(prev => ({
                                                                    ...prev,
                                                                    [idx]: { ...prev[idx], reason: val, description: '' }
                                                                }));
                                                            }}
                                                            options={[
                                                                { value: "Damaged item received", label: "⚠️ Damaged item received" },
                                                                { value: "Wrong item delivered", label: "📦 Wrong item delivered" },
                                                                { value: "Missing parts or accessories", label: "🧩 Missing parts or accessories" },
                                                                { value: "Defective / not working", label: "⚙️ Defective / not working" },
                                                                { value: "Other reason", label: "✏️ Other reason" }
                                                            ]}
                                                        />
                                                        {returnValidationErrors[idx]?.reason && (
                                                            <div style={{ color: 'red', fontSize: 10, marginTop: 4 }}>{returnValidationErrors[idx].reason}</div>
                                                        )}
                                                    </div>

                                                    {/* Describe issue (for any reason chosen) */}
                                                    {itemData.reason && (
                                                        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                                            <div style={{ fontSize: 10, fontWeight: 700, color: returnValidationErrors[idx]?.description ? 'red' : '#555', letterSpacing: 0.4, marginBottom: 4, textTransform: 'uppercase' }}>Describe the issue *</div>
                                                            <Input.TextArea
                                                                placeholder="Describe the issue in detail..."
                                                                status={returnValidationErrors[idx]?.description ? 'error' : ''}
                                                                value={itemData.description}
                                                                style={{ borderRadius: 6, fontSize: 11, resize: 'none', flexGrow: 1, minHeight: 48 }}
                                                                onChange={(e) => {
                                                                    setReturnItemData(prev => ({
                                                                        ...prev,
                                                                        [idx]: { ...prev[idx], description: e.target.value }
                                                                    }));
                                                                }}
                                                            />
                                                            {returnValidationErrors[idx]?.description && (
                                                                <div style={{ color: 'red', fontSize: 10, marginTop: 4 }}>{returnValidationErrors[idx].description}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>

                {/* Premium Footer: Refund Summary + Actions */}
                <div style={{ padding: '0 28px 28px', background: '#f8f9fc' }}>
                    {/* Summary Strip */}
                    <div
                        style={{
                            background: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '10px',
                            padding: '10px 14px',
                            marginBottom: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>

                            <div>
                                <div
                                    style={{
                                        fontSize: '10px',
                                        color: '#6b7280',
                                        fontWeight: 600,
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    Selected
                                </div>

                                <div
                                    style={{
                                        fontSize: '16px',
                                        fontWeight: 700,
                                        color: '#111827'
                                    }}
                                >
                                    {Object.values(returnItemData).filter(d => d?.selected).length}
                                </div>
                            </div>

                            <div
                                style={{
                                    width: '1px',
                                    height: '32px',
                                    background: '#e5e7eb'
                                }}
                            />

                            <div>
                                <div
                                    style={{
                                        fontSize: '10px',
                                        color: '#6b7280',
                                        fontWeight: 600,
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    Refund
                                </div>

                                <div
                                    style={{
                                        fontSize: '16px',
                                        fontWeight: 700,
                                        color: '#16a34a'
                                    }}
                                >
                                    ₹{Object.keys(returnItemData)
                                        .filter(i => returnItemData[i]?.selected)
                                        .reduce(
                                            (acc, i) =>
                                                acc +
                                                ((selectedOrder?.items?.[i]?.price || 0) *
                                                    (returnItemData[i]?.quantity || 1)),
                                            0
                                        )}
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                background: '#f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px'
                            }}
                        >
                            💳
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                        <button
                            onClick={() => setReturnModalOpen(false)}
                            style={{
                                width: 120, height: 36, borderRadius: 8, border: '1.5px solid #e8ecf0',
                                background: '#fff', color: '#555', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleOpenRefundDetails}
                            disabled={actionLoading}
                            style={{
                                width: 180, height: 36, borderRadius: 8, border: 'none',
                                background: actionLoading ? '#ccc' : 'linear-gradient(135deg, #ff8110, #ff5722)',
                                color: '#fff', fontWeight: 700, fontSize: 13, cursor: actionLoading ? 'not-allowed' : 'pointer',
                                boxShadow: actionLoading ? 'none' : '0 3px 10px rgba(255,129,16,0.35)',
                                transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                            }}
                        >
                            {actionLoading ? 'Submitting...' : '✓ Confirm Return'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Refund Destination Selection Popup Modal */}
            <Modal
                title={null}
                open={refundPopupOpen}
                onCancel={() => setRefundPopupOpen(false)}
                footer={null}
                width={500}
                centered
                styles={{ body: { padding: 0 }, content: { borderRadius: 20, overflow: 'hidden', padding: 0 } }}
            >
                {/* Premium Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    padding: '24px 32px 20px',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div style={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,129,16,0.15)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,129,16,0.2)', border: '1px solid rgba(255,129,16,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                            💳
                        </div>
                        <div>
                            <div style={{ color: '#fff', fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px' }}>Refund Method & Destination</div>
                            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 2 }}>Choose how you would like to receive your refund</div>
                        </div>
                    </div>
                </div>

                {/* Body with form */}
                <div style={{ padding: '24px', background: '#f8f9fc' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Select Refund Method:</label>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Choose refund method..."
                                value={selectedRefundMethod || undefined}
                                onChange={(val) => {
                                    setSelectedRefundMethod(val);
                                    setUseSavedDetails(true);
                                }}
                                options={[
                                    { value: 'WALLET', label: 'Wallet Credit (Immediate)' },
                                    ...(!['COD', 'CASH_ON_DELIVERY'].includes((selectedOrder?.paymentMethod || '').toUpperCase()) ? [
                                        { value: 'ORIGINAL_PAYMENT_METHOD', label: 'Original Payment Method (Razorpay Online Refund)' }
                                    ] : []),
                                    { value: 'UPI', label: 'UPI Transfer' },
                                    { value: 'BANK', label: 'Bank Account Transfer' }
                                ]}
                            />
                        </div>

                        {selectedRefundMethod === 'UPI' && (
                            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                                {savedProfile?.upiId ? (
                                    <div style={{ marginBottom: 12 }}>
                                        <Radio.Group onChange={(e) => setUseSavedDetails(e.target.value)} value={useSavedDetails}>
                                            <Space direction="vertical">
                                                <Radio value={true}>
                                                    Use Saved UPI ID: <Text strong>{savedProfile.upiId}</Text>
                                                </Radio>
                                                <Radio value={false}>Enter New UPI ID</Radio>
                                            </Space>
                                        </Radio.Group>
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 11, color: '#ff8110', marginBottom: 8 }}>No saved UPI ID found. Please enter details below.</div>
                                )}

                                {(!useSavedDetails || !savedProfile?.upiId) && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: refundValidationErrors.upiId ? 'red' : '#374151', marginBottom: 6 }}>UPI ID (e.g. username@bank):</label>
                                        <Input
                                            placeholder="Enter UPI ID"
                                            status={refundValidationErrors.upiId ? 'error' : ''}
                                            value={upiIdInput}
                                            onChange={(e) => setUpiIdInput(e.target.value)}
                                        />
                                        {refundValidationErrors.upiId && (
                                            <div style={{ color: 'red', fontSize: 10, marginTop: 4 }}>{refundValidationErrors.upiId}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedRefundMethod === 'BANK' && (
                            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                                {savedProfile?.bankDetails?.accountNumber ? (
                                    <div style={{ marginBottom: 12 }}>
                                        <Radio.Group onChange={(e) => setUseSavedDetails(e.target.value)} value={useSavedDetails}>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Radio value={true}>
                                                    <span style={{ fontWeight: 600, fontSize: 13 }}>Use Saved Bank Account</span>
                                                    <div style={{
                                                        marginTop: 8,
                                                        background: '#f0f9ff',
                                                        border: '1px solid #bae6fd',
                                                        borderRadius: 8,
                                                        padding: '10px 14px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 5
                                                    }}>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <span style={{ fontSize: 11, color: '#6b7280', width: 110 }}>Bank Name:</span>
                                                            <Text strong style={{ fontSize: 12, color: '#111827' }}>{savedProfile.bankDetails.bankName}</Text>
                                                        </div>
                                                        {savedProfile.bankDetails.accountHolderName && (
                                                            <div style={{ display: 'flex', gap: 8 }}>
                                                                <span style={{ fontSize: 11, color: '#6b7280', width: 110 }}>Account Holder:</span>
                                                                <Text strong style={{ fontSize: 12, color: '#111827' }}>{savedProfile.bankDetails.accountHolderName}</Text>
                                                            </div>
                                                        )}
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <span style={{ fontSize: 11, color: '#6b7280', width: 110 }}>Account Number:</span>
                                                            <Text strong style={{ fontSize: 12, color: '#111827', fontFamily: 'monospace', letterSpacing: 1 }}>{savedProfile.bankDetails.accountNumber}</Text>
                                                        </div>
                                                        {savedProfile.bankDetails.ifscCode && (
                                                            <div style={{ display: 'flex', gap: 8 }}>
                                                                <span style={{ fontSize: 11, color: '#6b7280', width: 110 }}>IFSC Code:</span>
                                                                <Text strong style={{ fontSize: 12, color: '#111827', fontFamily: 'monospace' }}>{savedProfile.bankDetails.ifscCode}</Text>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Radio>
                                                <Radio value={false}>Enter New Bank Account Details</Radio>
                                            </Space>
                                        </Radio.Group>
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 11, color: '#ff8110', marginBottom: 8 }}>No saved bank details found. Please enter details below.</div>
                                )}

                                {(!useSavedDetails || !savedProfile?.bankDetails?.accountNumber) && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: refundValidationErrors.bankName ? 'red' : '#374151', marginBottom: 4 }}>Bank Name:</label>
                                            <Input
                                                placeholder="e.g. HDFC Bank"
                                                status={refundValidationErrors.bankName ? 'error' : ''}
                                                value={bankDetailsInput.bankName}
                                                onChange={(e) => setBankDetailsInput(prev => ({ ...prev, bankName: e.target.value }))}
                                            />
                                            {refundValidationErrors.bankName && (
                                                <div style={{ color: 'red', fontSize: 10, marginTop: 4 }}>{refundValidationErrors.bankName}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: refundValidationErrors.accountHolderName ? 'red' : '#374151', marginBottom: 4 }}>Account Holder Name:</label>
                                            <Input
                                                placeholder="Name as in bank records"
                                                status={refundValidationErrors.accountHolderName ? 'error' : ''}
                                                value={bankDetailsInput.accountHolderName}
                                                onChange={(e) => setBankDetailsInput(prev => ({ ...prev, accountHolderName: e.target.value }))}
                                            />
                                            {refundValidationErrors.accountHolderName && (
                                                <div style={{ color: 'red', fontSize: 10, marginTop: 4 }}>{refundValidationErrors.accountHolderName}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: refundValidationErrors.accountNumber ? 'red' : '#374151', marginBottom: 4 }}>Account Number:</label>
                                            <Input
                                                placeholder="Bank account number"
                                                status={refundValidationErrors.accountNumber ? 'error' : ''}
                                                value={bankDetailsInput.accountNumber}
                                                onChange={(e) => setBankDetailsInput(prev => ({ ...prev, accountNumber: e.target.value }))}
                                            />
                                            {refundValidationErrors.accountNumber && (
                                                <div style={{ color: 'red', fontSize: 10, marginTop: 4 }}>{refundValidationErrors.accountNumber}</div>
                                            )}
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: refundValidationErrors.ifscCode ? 'red' : '#374151', marginBottom: 4 }}>IFSC Code:</label>
                                            <Input
                                                placeholder="e.g. HDFC0001234"
                                                status={refundValidationErrors.ifscCode ? 'error' : ''}
                                                value={bankDetailsInput.ifscCode}
                                                onChange={(e) => setBankDetailsInput(prev => ({ ...prev, ifscCode: e.target.value }))}
                                            />
                                            {refundValidationErrors.ifscCode && (
                                                <div style={{ color: 'red', fontSize: 10, marginTop: 4 }}>{refundValidationErrors.ifscCode}</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer with actions */}
                <div style={{ padding: '0 24px 24px', background: '#f8f9fc', display: 'flex', gap: 10, justifyContent: 'center' }}>
                    <button
                        onClick={() => setRefundPopupOpen(false)}
                        style={{
                            width: 120, height: 36, borderRadius: 8, border: '1.5px solid #e8ecf0',
                            background: '#fff', color: '#555', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        Back
                    </button>
                    <button
                        onClick={handleReturnRequest}
                        disabled={actionLoading}
                        style={{
                            width: 180, height: 36, borderRadius: 8, border: 'none',
                            background: actionLoading ? '#ccc' : 'linear-gradient(135deg, #ff8110, #ff5722)',
                            color: '#fff', fontWeight: 700, fontSize: 13, cursor: actionLoading ? 'not-allowed' : 'pointer',
                            boxShadow: actionLoading ? 'none' : '0 3px 10px rgba(255,129,16,0.35)',
                            transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                        }}
                    >
                        {actionLoading ? 'Submitting...' : '✓ Submit Request'}
                    </button>
                </div>
            </Modal>

            {/* Reschedule Modal */}
            <Modal
                title="Reschedule Your Delivery"
                open={rescheduleModalOpen}
                onCancel={() => setRescheduleModalOpen(false)}
                onOk={handleReschedule}
                okText="Confirm New Schedule"
                confirmLoading={actionLoading}
                width={500}
                okButtonProps={{
                    disabled: !rescheduleDate || !rescheduleSlot || (rescheduleSlot === 'custom' && !rescheduleTime)
                }}
            >
                <div className="pcu-padding-y-10">
                    <p className="pcu-margin-bottom-16 pcu-color-muted">
                        Choose a new date and time slot for your delivery.
                        Predefined slots are <Text strong className="pcu-color-success">FREE</Text>.
                    </p>

                    <div className="pcu-margin-bottom-20">
                        <Text strong className="pcu-display-block pcu-margin-bottom-8">Select Date</Text>
                        <DatePicker
                            className="pcu-w-100"
                            onChange={(date) => setRescheduleDate(date)}
                            value={rescheduleDate}
                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                        />
                    </div>

                    <div className="pcu-margin-bottom-20">
                        <Text strong className="pcu-display-block pcu-margin-bottom-8">Select Time Slot</Text>
                        <div className="pcu-flex pcu-flex-column pcu-gap-8">
                            {[
                                { val: '09:00 - 12:00', label: 'Morning (09:00 AM - 12:00 PM)', charge: 'Free', endHour: 12 },
                                { val: '13:00 - 16:00', label: 'Afternoon (01:00 PM - 04:00 PM)', charge: 'Free', endHour: 16 },
                                { val: '18:00 - 21:00', label: 'Evening (06:00 PM - 09:00 PM)', charge: 'Free', endHour: 21 },
                                { val: 'custom', label: 'Custom Time Slot', charge: '+₹100', endHour: 24 }
                            ].map(slot => {
                                const isToday = rescheduleDate && dayjs(rescheduleDate).isSame(dayjs(), 'day');
                                const isPast = isToday && dayjs().hour() >= slot.endHour;

                                return (
                                    <div
                                        key={slot.val}
                                        style={{
                                            padding: '12px',
                                            border: '1px solid',
                                            borderColor: rescheduleSlot === slot.val ? 'var(--nearzon-orange)' : '#eee',
                                            borderRadius: '8px',
                                            cursor: isPast ? 'not-allowed' : 'pointer',
                                            background: isPast ? '#f5f5f5' : (rescheduleSlot === slot.val ? 'rgba(255, 129, 16, 0.05)' : 'white'),
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            opacity: isPast ? 0.6 : 1
                                        }}
                                        onClick={() => !isPast && setRescheduleSlot(slot.val)}
                                    >
                                        <Space>
                                            <IconClock size={16} color={rescheduleSlot === slot.val ? 'var(--nearzon-orange)' : '#999'} />
                                            <Text strong={rescheduleSlot === slot.val}>{slot.label}</Text>
                                            {isPast && <Text type="danger" style={{ fontSize: 11 }}>(Already Passed)</Text>}
                                        </Space>
                                        <Tag color={slot.charge === 'Free' ? 'green' : 'volcano'}>{slot.charge}</Tag>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {rescheduleSlot === 'custom' && (
                        <div className="pcu-padding-15 pcu-bg-muted pcu-radius-8">
                            <Text strong className="pcu-display-block pcu-margin-bottom-8">Pick Custom Time</Text>
                            <TimePicker
                                className="pcu-w-100"
                                format="HH:mm"
                                use12Hours
                                minuteStep={30}
                                onChange={(time) => setRescheduleTime(time)}
                                value={rescheduleTime}
                                disabledTime={() => {
                                    const isToday = rescheduleDate && dayjs(rescheduleDate).isSame(dayjs(), 'day');
                                    if (!isToday) return {};
                                    return {
                                        disabledHours: () => Array.from({ length: dayjs().hour() }, (_, i) => i),
                                        disabledMinutes: (h) => h === dayjs().hour() ? Array.from({ length: dayjs().minute() }, (_, i) => i) : []
                                    };
                                }}
                            />
                        </div>
                    )}
                </div>
            </Modal>

            {/* ===== Issue Reported Details Modal ===== */}
            <Modal
                open={!!issueModalOrder}
                onCancel={() => {
                    setIssueModalOrder(null);
                    setUncheckedItems([]);
                    setRejectedIssueProducts([]);
                }}
                footer={null}
                title={null}
                width={900}
                className="issue-reported-modal"
                centered
                destroyOnClose
                bodyStyle={{ padding: 0, overflow: 'hidden' }}
            >
                {issueModalOrder && (
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', minHeight: 400 }}>
                        {/* LEFT COLUMN: Shop Products (40%) */}
                        {!isMobile && (
                            <div
                                className="custom-left-scrollbar"
                                style={{
                                    flex: '0 0 350px', // Fixed width for stability
                                    padding: '20px',
                                    backgroundColor: '#fafafa',
                                    overflowY: 'auto',
                                    maxHeight: '80vh',
                                    borderRight: '1px solid #f0f0f0',
                                    direction: 'rtl'
                                }}
                            >
                                <div style={{ direction: 'ltr', width: '100%' }}> {/* Ensure full width */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                        <Title level={5} style={{ margin: 0, fontSize: 14 }}>
                                            More from {issueModalOrder?.shopId?.name || 'this Shop'}
                                        </Title>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => {
                                                const shopId = typeof issueModalOrder?.shopId === 'object' ? issueModalOrder.shopId._id : issueModalOrder?.shopId;
                                                if (shopId) navigate(`/shop/${shopId}`);
                                            }}
                                            style={{ color: '#FF8800', fontSize: 12, fontWeight: 600 }}
                                        >
                                            View Shop
                                        </Button>
                                    </div>

                                    {fetchingProducts ? (
                                        <div style={{ padding: '40px 0', textAlign: 'center', color: '#999' }}>
                                            <IconClock className="pcu-spin pcu-margin-bottom-8" size={24} />
                                            <div>Loading suggestions...</div>
                                        </div>
                                    ) : shopProducts.length > 0 ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                                            {shopProducts.map(product => (
                                                <SuggestionProductCard
                                                    key={product._id}
                                                    product={product}
                                                    shopName={issueModalOrder?.shopId?.name || 'Local Shop'}
                                                    onAdd={(prod, variant) => {
                                                        const options = {};
                                                        if (variant) {
                                                            options[variant.optionName || prod.variantType || "Option"] = variant.val || variant.value;
                                                        }

                                                        const exists = addedProducts.find(p => p.product._id === prod._id && JSON.stringify(p.selectedOptions) === JSON.stringify(options));
                                                        if (exists) {
                                                            setAddedProducts(addedProducts.map(p => (p.product._id === prod._id && JSON.stringify(p.selectedOptions) === JSON.stringify(options)) ? { ...p, quantity: p.quantity + 1 } : p));
                                                        } else {
                                                            setAddedProducts([...addedProducts, { product: prod, quantity: 1, selectedOptions: options }]);
                                                        }
                                                        message.success(`Added ${prod.name}`);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                                            No suggestions found.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* RIGHT COLUMN: Issue Resolution (60%) */}
                        <div style={{ flex: 1.3, padding: isMobile ? '16px' : '24px', backgroundColor: '#fff' }}>
                            <div className="issue-modal-inner">
                                {/* Modal Header */}
                                <div className="issue-modal-header">
                                    <div className="issue-modal-header-icon">
                                        <IconAlertCircle size={22} />
                                    </div>
                                    <div className="issue-modal-header-text">
                                        <div className="issue-modal-title">Issue Reported by Seller</div>
                                        <div className="issue-modal-subtitle">
                                            Order #{issueModalOrder.orderId}
                                        </div>
                                    </div>
                                    <div className={`issue-modal-status-badge issue-status-${(issueModalOrder.productIssueRequest?.status || 'PENDING').toLowerCase()}`}>
                                        {issueModalOrder.productIssueRequest?.status || 'PENDING'}
                                    </div>
                                </div>

                                {/* Meta row — reported date */}
                                <div className="issue-modal-meta-row">
                                    <span className="issue-modal-meta-item">
                                        <IconCalendar size={13} />
                                        {issueModalOrder.productIssueRequest?.requestedAt
                                            ? dayjs(issueModalOrder.productIssueRequest.requestedAt).format('DD MMM YYYY, h:mm A')
                                            : '—'}
                                    </span>
                                </div>

                                {/* Issue Items List */}
                                <div className="issue-items-list pcu-margin-top-16">
                                    <div style={{ fontWeight: 600, marginBottom: 12 }}>Order Products ({issueModalOrder.items?.length || 0})</div>
                                    {(issueModalOrder.items || []).map((orderItem, idx) => {
                                        const productId = orderItem.product?._id || orderItem.product;
                                        const issueItem = issueModalOrder.productIssueRequest?.items?.find(
                                            reqItem => String(reqItem.product?._id || reqItem.product) === String(productId)
                                        );

                                        const productName = orderItem.name || orderItem.product?.name || 'Product';
                                        const productImage = orderItem.product?.imageUrl
                                            || (orderItem.product?.images?.length > 0 && orderItem.product.images[0]) || orderItem.imageUrl;

                                        if (!issueItem) {
                                            // Normal Product Render
                                            const isChecked = !uncheckedItems.includes(productId);
                                            const isPending = (issueModalOrder.productIssueRequest?.status === 'PENDING' || !issueModalOrder.productIssueRequest?.status);
                                            return (
                                                <div key={idx} className={`pcu-flex pcu-items-center pcu-gap-12 pcu-padding-12 pcu-border pcu-radius-8 pcu-margin-bottom-8 pcu-transition-all ${!isChecked ? 'pcu-opacity-50 pcu-bg-muted' : ''} ${isPending ? 'pcu-cursor-pointer' : ''}`}
                                                    onClick={() => {
                                                        if (!isPending) return;
                                                        if (isChecked) {
                                                            setUncheckedItems([...uncheckedItems, productId]);
                                                        } else {
                                                            setUncheckedItems(uncheckedItems.filter(id => id !== productId));
                                                        }
                                                    }}>
                                                    <Checkbox checked={isChecked} disabled={!isPending} />
                                                    <SafeImage src={productImage} fallbackSrc={null} alt={productName} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, filter: !isChecked ? 'grayscale(1)' : 'none' }} />
                                                    <div className="pcu-flex-1 pcu-flex pcu-justify-between pcu-items-center">
                                                        <div className="pcu-min-w-0">
                                                            <div style={{ fontWeight: 500, color: '#333', fontSize: 14, textDecoration: !isChecked ? 'line-through' : 'none' }}>{productName}</div>
                                                            {(() => {
                                                                let opts = orderItem.selectedOptions || orderItem.options;
                                                                if (typeof opts === 'string' && opts.startsWith('{')) {
                                                                    try { opts = JSON.parse(opts); } catch (e) { }
                                                                }

                                                                if (opts && typeof opts === 'object' && !Array.isArray(opts) && Object.keys(opts).length > 0) {
                                                                    return (
                                                                        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                                                                            {Object.entries(opts).map(([k, v]) => `${k}: ${localFormatUnit(v, k)}`).join(', ')}
                                                                        </div>
                                                                    );
                                                                }

                                                                const unitInfo = (typeof opts === 'string' && opts.length > 0 && !opts.startsWith('{')) ? opts :
                                                                    (orderItem.variant || orderItem.variantName || orderItem.unit || orderItem.weight ||
                                                                        orderItem.description || orderItem.product?.unit || orderItem.product?.weight || orderItem.product?.description);

                                                                if (unitInfo && String(unitInfo).length < 40) {
                                                                    return <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{unitInfo}</div>;
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                        <div style={{ textAlign: 'right', minWidth: 70 }}>
                                                            <div style={{ fontWeight: 700, color: '#333', fontSize: 14 }}>{formatPrice(orderItem.price)}</div>
                                                            <div style={{ fontSize: 11, color: '#888' }}>Qty: {orderItem.quantity}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // Issue Product Render
                                        const displayReason = issueItem.reason === 'Custom reason' ? issueItem.customReason : issueItem.reason;


                                        const isRejected = rejectedIssueProducts.includes(productId);
                                        const isPending = (issueModalOrder.productIssueRequest?.status === 'PENDING' || !issueModalOrder.productIssueRequest?.status);

                                        return (
                                            <div key={idx} className="issue-item-card" style={{
                                                border: isRejected ? '1px solid #d9d9d9' : '1px solid #ffa39e',
                                                backgroundColor: isRejected ? '#fafafa' : '#fff1f0',
                                                position: 'relative',
                                                overflow: 'visible',
                                                zIndex: 1,
                                                marginBottom: 16,
                                                opacity: isRejected ? 0.7 : 1,
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                borderRadius: 16,
                                                boxShadow: isRejected ? 'none' : '0 4px 12px rgba(255, 77, 79, 0.08)'
                                            }}>
                                                {!isRejected && (
                                                    <div style={{ position: 'absolute', top: -10, right: 12, background: '#ff4d4f', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 10px', borderRadius: 12, boxShadow: '0 2px 4px rgba(255, 77, 79, 0.3)', zIndex: 2, letterSpacing: 0.5 }}>
                                                        ISSUE REPORTED
                                                    </div>
                                                )}
                                                {isRejected && (
                                                    <div style={{ position: 'absolute', top: -10, right: 12, background: '#8c8c8c', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 10px', borderRadius: 12, zIndex: 2, letterSpacing: 0.5 }}>
                                                        TO BE REMOVED
                                                    </div>
                                                )}
                                                {/* Product identity row - ensuring full context for the issue item */}
                                                <div className="issue-item-product-row" style={{ borderBottom: '1px solid rgba(255, 163, 158, 0.4)', padding: '16px', display: 'flex', gap: 12, alignItems: 'center' }}>
                                                    <SafeImage
                                                        src={productImage}
                                                        fallbackSrc={null}
                                                        alt={productName}
                                                        style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, border: '1px solid rgba(0,0,0,0.06)', filter: isRejected ? 'grayscale(1)' : 'none' }}
                                                    />
                                                    <div className="issue-item-product-info" style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div className="pcu-min-w-0">
                                                            <div style={{ fontWeight: 700, color: '#1c1917', fontSize: 16, textDecoration: isRejected ? 'line-through' : 'none', marginBottom: 2 }}>
                                                                {productName}
                                                            </div>
                                                            {(() => {
                                                                let opts = orderItem.selectedOptions || orderItem.options;

                                                                if (opts && typeof opts === 'object' && !Array.isArray(opts) && Object.keys(opts).length > 0) {
                                                                    return (
                                                                        <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                                                                            {Object.entries(opts).map(([k, v]) => `${k}: ${localFormatUnit(v, k)}`).join(', ')}
                                                                        </div>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Details grid */}
                                                {!isRejected && (
                                                    <div className="issue-item-details-grid" style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', position: 'relative', zIndex: 1 }}>
                                                        <div className="issue-detail-cell">
                                                            <span className="issue-detail-label">Reported Qty</span>
                                                            <span className="issue-detail-value">{orderItem.quantity} unit{orderItem.quantity !== 1 ? 's' : ''}</span>
                                                        </div>
                                                        {issueItem.type === 'UPDATE_QTY' && issueItem.newValue != null && (
                                                            <div className="issue-detail-cell">
                                                                <span className="issue-detail-label">Price</span>
                                                                <span className="issue-detail-value highlight" style={{ fontWeight: 800, color: '#FF8800', fontSize: 18 }}>{formatPrice(orderItem.price)}</span>
                                                            </div>
                                                        )}
                                                        <div className="issue-detail-cell full-width" style={{ borderTop: '1px solid rgba(255, 163, 158, 0.4)' }}>
                                                            <span className="issue-detail-label">Report Reason</span>
                                                            <span className="issue-detail-value">{displayReason || '—'}</span>
                                                        </div>
                                                        {issueItem.customReason && issueItem.reason !== 'Custom reason' && (
                                                            <div className="issue-detail-cell full-width" style={{ borderTop: '1px solid rgba(255, 163, 158, 0.4)' }}>
                                                                <span className="issue-detail-label">Seller Message</span>
                                                                <span className="issue-detail-value seller-msg">"{issueItem.customReason}"</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Granular Action Footer */}
                                                {isPending && (
                                                    <div className="pcu-padding-12 pcu-flex pcu-justify-end pcu-gap-8 pcu-bg-white" style={{ borderRadius: '0 0 14px 14px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                                        {isRejected ? (
                                                            <Button
                                                                size="small"
                                                                type="dashed"
                                                                onClick={() => setRejectedIssueProducts(rejectedIssueProducts.filter(id => id !== productId))}
                                                                className="pcu-radius-6 pcu-font-12"
                                                            >
                                                                {issueItem.type === 'REMOVE' ? 'Remove Item' : 'Keep Item'}
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                size="small"
                                                                danger
                                                                onClick={() => setRejectedIssueProducts([...rejectedIssueProducts, productId])}
                                                                className="pcu-radius-6 pcu-font-12"
                                                            >
                                                                {issueItem.type === 'REMOVE' ? 'Keep Item (Wait for it)' : 'Remove Item'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {/* Added Replacements List */}
                                    {addedProducts.map((addReq, idx) => (
                                        <div key={`added-${idx}`} className="pcu-flex pcu-items-center pcu-gap-12 pcu-padding-12 pcu-border pcu-radius-8 pcu-margin-bottom-8 pcu-bg-white" style={{ borderStyle: 'dashed', borderColor: '#FF8800' }}>
                                            <div style={{ width: 16 }}></div> {/* Placeholder for Checkbox alignment */}
                                            <SafeImage src={addReq.product.images?.[0] || addReq.product.imageUrl} fallbackSrc={null} alt={addReq.product.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }} />
                                            <div className="pcu-flex-1">
                                                <div style={{ fontWeight: 500, color: '#333' }}>
                                                    {addReq.product.name}
                                                    <Tag color="orange" style={{ fontSize: 9, marginLeft: 6, borderRadius: 4, padding: '0 4px', lineHeight: '14px', border: 'none' }}>NEW</Tag>
                                                </div>
                                                {(() => {
                                                    let opts = addReq.selectedOptions || addReq.options;

                                                    if (opts && typeof opts === 'object' && !Array.isArray(opts) && Object.keys(opts).length > 0) {
                                                        return (
                                                            <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                                                                {Object.entries(opts).map(([k, v]) => `${k}: ${localFormatUnit(v, k)}`).join(', ')}
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                                <div style={{ fontSize: 13, color: '#666' }}>
                                                    Qty: {addReq.quantity}
                                                    {addReq.selectedOptions && Object.entries(addReq.selectedOptions).length > 0 && (
                                                        <span style={{ marginLeft: 8, fontStyle: 'italic', color: '#999' }}>
                                                            ({Object.entries(addReq.selectedOptions).map(([k, v]) => `${v}`).join(', ')})
                                                        </span>
                                                    )}
                                                    <span style={{ marginLeft: 8, fontWeight: 600, color: '#FF8800' }}>{formatPrice(addReq.product.price * addReq.quantity)}</span>
                                                </div>
                                            </div>
                                            <Button
                                                size="small"
                                                type="text"
                                                danger
                                                icon={<CloseCircleOutlined />}
                                                onClick={() => setAddedProducts(addedProducts.filter((_, i) => i !== idx))}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* Modal Footer Actions - Only show if pending */}
                                {(issueModalOrder.productIssueRequest?.status === 'PENDING' || !issueModalOrder.productIssueRequest?.status) && (
                                    <div className="pcu-margin-top-12 pcu-padding-16 pcu-flex pcu-justify-end pcu-gap-12 pcu-bg-white" style={{ borderTop: '1px solid #f0f0ef', position: 'sticky', bottom: 0, zIndex: 10 }}>
                                        <Button
                                            danger
                                            size="large"
                                            style={{ borderRadius: 8, fontWeight: 600 }}
                                            onClick={() => handleProductIssueRespond('REJECT')}
                                            loading={actionLoading}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="large"
                                            style={{ background: '#FF8800', borderColor: '#FF8800', borderRadius: 8, fontWeight: 600, boxShadow: '0 4px 10px rgba(255, 136, 0, 0.2)' }}
                                            onClick={() => handleProductIssueRespond('ACCEPT')}
                                            loading={actionLoading}
                                        >
                                            {rejectedIssueProducts.length === 0 && uncheckedItems.length === 0
                                                ? "Replace Order"
                                                : "Accept & Continue with Selection"}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>



        </div>
    );
};

export default CustomerOrders;

